import React, { useEffect, useLayoutEffect, useState, useContext, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { db, auth, functions } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { openCheckout, openExternalUrl } from '../services/nativeBrowser';
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { UserContext } from '../contexts/UserContext';
import FeedbackForm from '../components/FeedbackForm';
import AddressRequiredModal from '../components/AddressRequiredModal';
import ImageOverlay from '../components/ImageOverlay';
import { useContact } from '../contexts/ContactContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  formatPurchaseTransferStatus,
  getPurchasePrice,
  getSellerPayoutCents,
  isZeroNetEscrowHold,
} from '../utils/purchasePayoutDisplay';
import { getPayoutReleaseStatus } from '../utils/escrowDisplay';
import PayoutReleaseBadge from '../components/PayoutReleaseBadge';
import ShippingPricesModal, { ShippingPricesLink } from '../components/ShippingPricesModal';
import { placeholderFor } from '../utils/listingPlaceholder';

// Teal check badge — matches the status check marks used on TradeProgress
// (color #4db6ac + crisp SVG check, flexbox-centered) so the two progress pages
// stay visually consistent. Sized inline for status labels.
const TealCheck = ({ size = 14 }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: `${size}px`, height: `${size}px`, borderRadius: '50%', backgroundColor: '#4db6ac', verticalAlign: 'middle', marginRight: '4px', flexShrink: 0 }}>
    <svg width={Math.round(size * 0.57)} height={Math.round(size * 0.57)} viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
  </span>
);

const PurchaseProgress = () => {
  console.log('🚀 PurchaseProgress component mounting...');
  const { offerId } = useParams();
  console.log('🚀 PurchaseProgress offerId:', offerId);
  const { notifications, markAsRead } = useNotifications();

  // Mark purchase progress notifications as read when viewing this purchase
  useEffect(() => {
    const markPurchaseProgressNotificationsRead = async () => {
      const purchaseProgressNotifications = notifications.filter(
        n => (n.type === 'tracking_added_purchase' ||
              n.type === 'package_delivered_purchase' ||
              n.type === 'purchase_complete' ||
              n.type === 'step_reminder') &&
             // step_reminder writes the id in both places; the others only top-level.
             (n.purchaseId || n.metadata?.purchaseId) === offerId &&
             !n.read
      );
      
      if (purchaseProgressNotifications.length > 0) {
        console.log(`📧 Marking ${purchaseProgressNotifications.length} purchase progress notification(s) as read`);
        for (const notification of purchaseProgressNotifications) {
          await markAsRead(notification.id);
        }
      }
    };

    if (notifications.length > 0 && offerId) {
      markPurchaseProgressNotificationsRead();
    }
  }, [notifications, markAsRead, offerId]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openContact } = useContact();
  const [offerData, setOfferData] = useState(null);
  const [targetPedal, setTargetPedal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  // Distinguish "still resolving auth / fetching" from "genuinely missing" so we
  // never flash "Purchase not found" during load or pull-to-refresh (full reload).
  const [authChecked, setAuthChecked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [buyerUsername, setBuyerUsername] = useState('');
  const [sellerUsername, setSellerUsername] = useState('');
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [collapsedSteps, setCollapsedSteps] = useState({});
  
  // Shipping label state
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [showShippingPrices, setShowShippingPrices] = useState(false);
  const [packageData, setPackageData] = useState({
    length: '',
    width: '',
    height: '',
    weight: '',
    fromAddress: {
      name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      email: '',
      phone: ''
    },
    toAddress: {
      name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      email: '',
      phone: ''
    }
  });
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [gettingRates, setGettingRates] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [photoOverlay, setPhotoOverlay] = useState({ isOpen: false, url: '', alt: '', photos: [], index: 0 });

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [offerId]);

  // Once per purchase, not on every refetch: depending on `offerData` scrolled
  // the user back to the top whenever the page reloaded its data (same bug as
  // TradeProgress).
  const scrolledForOfferRef = useRef(null);
  const offerLoaded = !loading && !!offerData;
  useEffect(() => {
    if (!offerLoaded || scrolledForOfferRef.current === offerId) return;
    scrolledForOfferRef.current = offerId;
    window.scrollTo(0, 0);
  }, [offerLoaded, offerId]);

  const maskEmail = (email) => {
    if (!email) return '';
    const dotIndex = email.lastIndexOf('.');
    if (dotIndex === -1) return '*****';
    return '*****@*****' + email.slice(dotIndex);
  };

  const maskPhone = (phone) => {
    if (!phone) return '';
    return '(***) ***-****';
  };

  // Auto-populate addresses when offer data is loaded
  useEffect(() => {
    const populateAddresses = async () => {
      if (!offerData || !auth.currentUser) return;
      
      try {
        // Get current user's data for fromAddress
        const currentUserDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        const currentUserData = currentUserDoc.exists ? currentUserDoc.data() : {};
        
        // 🔧 FIX: Use firstName + lastName for current user if available
        let currentUserFullName = shippingAddress?.name || '';
        if (currentUserData.firstName && currentUserData.lastName) {
          currentUserFullName = `${currentUserData.firstName} ${currentUserData.lastName}`;
        }
        
        // Get buyer's data for toAddress from the snapshot baked onto the
        // offer doc at acceptance (PII isolation phase 1, group B). Falls
        // back to a direct users/ read for legacy offers whose snapshot
        // hasn't been backfilled — that fallback will fail post-rule-flip.
        let buyerData = offerData.buyerSnapshot;
        if (!buyerData) {
          console.warn('📍 No buyerSnapshot on purchase offer; falling back to users/ read');
          const buyerDoc = await getDoc(doc(db, 'users', offerData.initiatorId));
          buyerData = buyerDoc.exists ? buyerDoc.data() : {};
        }

        let buyerFullName = buyerData.shippingAddress?.name || '';
        if (buyerData.firstName && buyerData.lastName) {
          buyerFullName = `${buyerData.firstName} ${buyerData.lastName}`;
        }

        setPackageData(prev => ({
          ...prev,
          fromAddress: {
            ...prev.fromAddress,
            ...shippingAddress,
            name: currentUserFullName,
            country: shippingAddress?.country || 'US',
            email: auth.currentUser?.email || '',
            phone: currentUserData.phoneNumber || ''
          },
          toAddress: {
            ...prev.toAddress,
            ...buyerData.shippingAddress,
            name: buyerFullName,
            country: buyerData.shippingAddress?.country || 'US',
            email: buyerData.email || '',
            phone: buyerData.phoneNumber || ''
          }
        }));
      } catch (error) {
        console.error('Error auto-populating addresses:', error);
      }
    };

    populateAddresses();
  }, [offerData, auth.currentUser]);

  const { shippingAddress, firestoreUser, loading: userLoading, refreshUserData } = useContext(UserContext);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // A user without a complete address previously got silently blank From-
  // Address fields on the Shippo label form. Auto-open the same inline modal
  // the offer pages use so they can fill it in place (found during Android
  // testing, 2026-08-20). handleOpenShippingForm reads the context address at
  // click time, so a save here flows into the label form with no extra wiring.
  useEffect(() => {
    if (!userLoading && !isAddressComplete(shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress, userLoading]);

  // Address validation helper (copied from TradeProgress)
  const isAddressComplete = (address) => {
    return address && 
           address.name && 
           address.street1 && 
           address.city && 
           address.state && 
           address.zip;
  };

  const toggleStepCollapse = (stepIndex) => {
    setCollapsedSteps(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  const openConfirmationPhoto = (url, userName) => {
    setPhotoOverlay({
      isOpen: true,
      url,
      alt: `${userName}'s confirmation photo`,
      photos: [url],
      index: 0,
    });
  };

  const closeConfirmationPhoto = () => {
    setPhotoOverlay({ isOpen: false, url: '', alt: '', photos: [], index: 0 });
  };

  // Shipping form functions (copied from TradeProgress)
  const handleGetShippingRates = async () => {
    setGettingRates(true);
    
    const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-pedalxchange.cloudfunctions.net';
    const fullUrl = `${functionsUrl}/getShippoRates`;
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          tradeId: offerId,
          fromAddress: packageData.fromAddress,
          toAddress: packageData.toAddress,
          parcel: {
            length: parseFloat(packageData.length),
            width: parseFloat(packageData.width),
            height: parseFloat(packageData.height),
            weight: parseFloat(packageData.weight),
            distance_unit: 'in',
            mass_unit: 'lb',
            value: offerData.acceptedAmount || offerData.offerAmount || 100 // Use actual purchase price for insurance
          },
          userId: currentUser.uid
        })
      });

      const result = await response.json();
      
      if (result.success && result.rates) {
        setShippingRates(result.rates);
        console.log('✅ Retrieved shipping rates:', result.rates);
      } else {
        console.error('❌ Error getting shipping rates:', result.error);
        alert('Error getting shipping rates: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error getting shipping rates:', error);
      alert('Error getting shipping rates: ' + error.message + 
            '\n\nPlease check:\n' +
            '• Package dimensions are filled out\n' +
            '• All address fields are complete\n' +
            '• You have internet connectivity\n' +
            '• Try again in a few seconds');
    }
    setGettingRates(false);
  };

  const handleCreateCheckout = async (rate) => {
    setCreatingCheckout(true);
    console.log('🚀 Starting checkout creation for rate:', rate);
    console.log('🚀 Offer ID:', offerId);
    console.log('🚀 User ID:', currentUser.uid);
    
    try {
      const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-pedalxchange.cloudfunctions.net';
      const fullUrl = `${functionsUrl}/createLabelCheckoutSession`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          tradeId: offerId,
          rateObjectId: rate.rateObjectId,
          totalAmount: rate.amount,
          rateDetails: {
            provider: rate.provider,
            servicelevel: rate.servicelevel,
            originalAmount: rate.shippoRate || (rate.amount - 2),
            markupAmount: rate.platformFee || 2.00
          },
          userId: currentUser.uid,
          insuranceAmount: offerData?.acceptedAmount || offerData?.offerAmount || 100,
          returnTo: Capacitor.isNativePlatform() ? 'app' : undefined
        })
      });

      const result = await response.json();
      
      if (result.success && result.checkoutUrl) {
        console.log('✅ Checkout URL created:', result.checkoutUrl);
        // Native: in-app browser sheet. `onReturn` ONLY fires on native — on
        // web openCheckout does a full-page redirect and Stripe's success_url
        // handles the return. This page has NO live listener (refreshPurchaseData
        // is a one-time getDoc), so on native we must refetch ourselves when
        // the sheet closes. The label is written by the Stripe webhook ->
        // Shippo purchase, which takes a few seconds, so poll until
        // shippingStatus.labelCreated lands (or attempts run out; the user can
        // also pull to refresh). Mirrors the same handler in TradeProgress.
        await openCheckout(result.checkoutUrl, {
          onReturn: () => {
            let attempts = 0;
            const maxAttempts = 6;
            const poll = async () => {
              attempts += 1;
              try {
                await refreshPurchaseData();
                const snap = await getDoc(doc(db, 'purchaseOffers', offerId));
                const labelCreated = snap.exists() && snap.data().shippingStatus?.labelCreated;
                if (labelCreated) {
                  // Label confirmed — close the shipping form + rate picker. These
                  // are LOCAL state, so the refetch alone doesn't clear them; only
                  // web's full-page redirect remounts the component. Without this
                  // the stale "Select Shipping Option" list sits under the
                  // purchased-label card on native.
                  setShowShippingForm(false);
                  setShippingRates([]);
                  setSelectedRate(null);
                } else if (attempts < maxAttempts) {
                  setTimeout(poll, 2000);
                }
              } catch (err) {
                console.error('❌ Error refetching purchase after label checkout:', err);
                if (attempts < maxAttempts) setTimeout(poll, 2000);
              }
            };
            setTimeout(poll, 1500);
          }
        });
      } else {
        console.error('❌ Checkout creation failed:', result.error);
        alert('Error creating checkout: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error creating checkout:', error);
      console.error('❌ Error stack:', error.stack);
      alert('Payment setup failed: Internal error\n\nDetails: ' + error.message);
    }
    setCreatingCheckout(false);
  };

  const handleOpenShippingForm = async () => {
    // Auto-fill fromAddress with user's saved shippingAddress if not already filled
    if (isAddressComplete(shippingAddress)) {
      // Get current user's email, phone, and full name from Firestore
      const currentUserEmail = auth.currentUser?.email || '';
      
      // Get current user's phone and full name from Firestore
      let currentUserPhone = '';
      let currentUserFullName = shippingAddress.name || ''; // Fallback to shippingAddress.name
      try {
        const currentUserDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (currentUserDoc.exists()) {
          const currentUserData = currentUserDoc.data();
          currentUserPhone = currentUserData.phoneNumber || '';
          
          // 🔧 FIX: Use firstName + lastName if available, otherwise use shippingAddress.name
          if (currentUserData.firstName && currentUserData.lastName) {
            currentUserFullName = `${currentUserData.firstName} ${currentUserData.lastName}`;
          }
        }
      } catch (err) {
        console.error('Failed to fetch current user phone:', err);
      }
      
      setPackageData(prev => ({
        ...prev,
        fromAddress: {
          ...prev.fromAddress,
          ...shippingAddress,
          name: currentUserFullName, // 🔧 FIX: Use full name instead of shippingAddress.name
          country: shippingAddress.country || 'US',
          email: currentUserEmail,
          phone: currentUserPhone || ''
        }
      }));
      
      console.log('🏠 Auto-filled fromAddress with full name, email and phone:', { name: currentUserFullName, email: currentUserEmail, phone: currentUserPhone });
    }
    
    // Auto-fill toAddress with buyer's data from the snapshot baked onto the
    // offer doc at acceptance (PII isolation phase 1, group B).
    try {
      let buyerData = offerData.buyerSnapshot;
      if (!buyerData) {
        console.warn('📍 No buyerSnapshot on purchase offer; falling back to users/ read');
        const buyerDoc = await getDoc(doc(db, 'users', offerData.initiatorId));
        buyerData = buyerDoc.exists ? buyerDoc.data() : {};
      }

      let buyerFullName = buyerData.shippingAddress?.name || '';
      if (buyerData.firstName && buyerData.lastName) {
        buyerFullName = `${buyerData.firstName} ${buyerData.lastName}`;
      }

      setPackageData(prev => ({
        ...prev,
        toAddress: {
          ...prev.toAddress,
          ...buyerData.shippingAddress,
          name: buyerFullName,
          country: buyerData.shippingAddress?.country || 'US',
          email: buyerData.email || '',
          phone: buyerData.phoneNumber || ''
        }
      }));
    } catch (error) {
      console.error('Error fetching buyer data for shipping form:', error);
    }
    
    setShowShippingForm(true);
  };

  const refreshPurchaseData = useCallback(async () => {
    if (!offerId) return;
    
    setRefreshing(true);
    try {
      const offerRef = doc(db, 'purchaseOffers', offerId);
      const offerSnap = await getDoc(offerRef);
      
      if (offerSnap.exists()) {
        const data = offerSnap.data();
        setNotFound(false);
        setOfferData({ id: offerSnap.id, ...data });
        
        // Load target pedal
        const targetPedalSnap = await getDoc(doc(db, 'listings', data.targetPedalId));
        if (targetPedalSnap.exists()) {
          setTargetPedal({ id: targetPedalSnap.id, ...targetPedalSnap.data() });
        }
        
        // Load usernames — prefer offer-doc snapshot, fall back to publicProfiles.
        const buyerSnapshotName = data.buyerSnapshot?.username;
        const sellerSnapshotName = data.sellerSnapshot?.username;
        if (buyerSnapshotName) {
          setBuyerUsername(buyerSnapshotName);
        } else {
          const buyerSnap = await getDoc(doc(db, 'publicProfiles', data.initiatorId));
          if (buyerSnap.exists()) setBuyerUsername(buyerSnap.data().username || 'Unknown User');
        }
        if (sellerSnapshotName) {
          setSellerUsername(sellerSnapshotName);
        } else {
          const sellerSnap = await getDoc(doc(db, 'publicProfiles', data.responderId));
          if (sellerSnap.exists()) setSellerUsername(sellerSnap.data().username || 'Unknown User');
        }
      }
    } catch (error) {
      console.error('Error refreshing purchase data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [offerId]);
  
  // Handle refresh status button click
  const handleRefreshStatus = async () => {
    setRefreshing(true);
    try {
      console.log('🔄 Refreshing purchase status...');
      const refreshPurchaseStatus = httpsCallable(functions, 'refreshPurchaseStatus');
      const result = await refreshPurchaseStatus({ purchaseId: offerId });
      
      console.log('✅ Refresh result:', result.data);
      
      if (result.data.statusChanged) {
        // Refetch the purchase data to show the updated status
        const offerRef = doc(db, 'purchaseOffers', offerId);
        const offerSnap = await getDoc(offerRef);
        if (offerSnap.exists()) {
          const updatedOffer = { id: offerSnap.id, ...offerSnap.data() };
          setOfferData(updatedOffer);
          console.log('📊 Purchase data refreshed');
        }
        
        if (result.data.message) {
          alert(result.data.message);
        }
      } else if (result.data.message) {
        alert(result.data.message);
      }
    } catch (error) {
      console.error('❌ Error refreshing status:', error);
      alert(`Error: ${error.message || 'Failed to refresh status'}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Refresh tracking status from Shippo
  const refreshPurchaseTrackingStatus = async () => {
    try {
      setRefreshing(true);
      console.log('🔄 Refreshing purchase tracking status from Shippo...');
      
      const refreshFunction = httpsCallable(functions, 'refreshPurchaseTracking');
      const result = await refreshFunction({ purchaseId: offerId });
      
      console.log('✅ Tracking refresh result:', result.data);
      
      // Refresh the purchase data to show updated tracking
      await refreshPurchaseData();
      
      alert('Tracking status updated successfully!');
    } catch (error) {
      console.error('❌ Error refreshing tracking:', error);
      alert(`Failed to refresh tracking: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };
  
  // Helper function to update purchase step completion timestamp
  const updatePurchaseStepTimestamp = async (stepId) => {
    if (!userId) return;
    
    try {
      const updatePurchaseStepTimestampFn = httpsCallable(functions, 'updatePurchaseStepTimestamp');
      await updatePurchaseStepTimestampFn({
        purchaseId: offerId,
        stepId: stepId,
        userId: userId
      });
      console.log(`✅ Updated timestamp for purchase step: ${stepId}`);
    } catch (error) {
      console.error(`❌ Error updating timestamp for purchase step ${stepId}:`, error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (user) {
        await refreshPurchaseData();
      }
    });

    return () => unsubscribe();
  }, [refreshPurchaseData]);

  // Automatically refresh tracking when page loads (if tracking number exists and not delivered)
  // Use a ref to ensure this only runs ONCE per page load
  const hasAutoRefreshed = React.useRef(false);
  
  useEffect(() => {
    console.log('🔍 Auto-refresh useEffect triggered', {
      offerData: !!offerData,
      hasTracking: !!offerData?.shippingStatus?.trackingNumber,
      trackingNumber: offerData?.shippingStatus?.trackingNumber,
      delivered: offerData?.shippingStatus?.delivered,
      hasAutoRefreshed: hasAutoRefreshed.current
    });
    
    const hasTracking = offerData?.shippingStatus?.trackingNumber;
    const delivered = offerData?.shippingStatus?.delivered;
    
    // Only auto-refresh ONCE when data first loads with tracking info
    if (hasTracking && !delivered && offerData && !hasAutoRefreshed.current) {
      console.log('✅ CONDITIONS MET - Auto-refreshing purchase tracking on page load...');
      hasAutoRefreshed.current = true; // Mark as refreshed
      refreshPurchaseTrackingStatus(); // Show alert like TradeProgress does
    } else {
      console.log('❌ CONDITIONS NOT MET - Skipping auto-refresh', {
        hasTracking,
        delivered,
        hasOfferData: !!offerData,
        alreadyRefreshed: hasAutoRefreshed.current
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData?.shippingStatus?.trackingNumber, offerData?.shippingStatus?.delivered, offerData]);

  const loadPurchaseData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading purchase data for offerId:', offerId);
      console.log('🔄 Current user:', auth.currentUser?.uid);
      
      // Load purchase offer data
      const offerRef = doc(db, 'purchaseOffers', offerId);
      const offerSnap = await getDoc(offerRef);
      
      if (!offerSnap.exists()) {
        console.error('❌ Purchase offer not found:', offerId);
        setNotFound(true);
        return;
      }
      
      console.log('✅ Purchase offer found:', offerSnap.data());
      
      const data = offerSnap.data();
      setNotFound(false);
      setOfferData({ id: offerSnap.id, ...data });
      
      // Load target pedal
      const targetPedalSnap = await getDoc(doc(db, 'listings', data.targetPedalId));
      if (targetPedalSnap.exists()) {
        setTargetPedal({ id: targetPedalSnap.id, ...targetPedalSnap.data() });
      }
      
      // Load usernames — prefer offer-doc snapshot, fall back to publicProfiles.
      const buyerSnapshotName = data.buyerSnapshot?.username;
      const sellerSnapshotName = data.sellerSnapshot?.username;
      if (buyerSnapshotName) {
        setBuyerUsername(buyerSnapshotName);
      } else {
        const buyerSnap = await getDoc(doc(db, 'publicProfiles', data.initiatorId));
        if (buyerSnap.exists()) setBuyerUsername(buyerSnap.data().username || 'Unknown User');
      }
      if (sellerSnapshotName) {
        setSellerUsername(sellerSnapshotName);
      } else {
        const sellerSnap = await getDoc(doc(db, 'publicProfiles', data.responderId));
        if (sellerSnap.exists()) setSellerUsername(sellerSnap.data().username || 'Unknown User');
      }
      
    } catch (error) {
      console.error('❌ Error loading purchase data:', error);
      console.error('❌ Error details:', error.message, error.code);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect triggered, calling loadPurchaseData for offerId:', offerId);
    loadPurchaseData();
  }, [offerId]);

  // Handle auto-collapse of completed steps
  useEffect(() => {
    if (!offerData) return;
    
    // Only calculate if we have user data
    if (!currentUser) return;
    
    const isBuyer = currentUser?.uid === offerData.initiatorId;
    
    const buyer = {
      paid: offerData.paymentStatus?.buyerPaid || false,
      photoUploaded: false, // Buyer doesn't upload photos in purchases
      labelPurchased: false, // Buyer doesn't purchase labels in purchases
      delivered: offerData.shippingStatus?.delivered || false,
    };
    
    const seller = {
      paid: false, // Seller doesn't pay in purchases
      photoUploaded: offerData.shippingStatus?.confirmationPhoto ? true : false,
      labelPurchased: offerData.shippingStatus?.labelCreated || offerData.shippingStatus?.labelPurchased || false,
      delivered: false, // Seller doesn't mark as delivered
    };
    
    // Initialize collapsed state based on main steps definition
    const initialCollapsedState = {};
    const mainSteps = [
      {
        label: 'Completed Payment',
        buyerCompleted: offerData.paymentStatus?.buyerPaid || false,
        sellerCompleted: offerData.paymentStatus?.sellerPaid || false,
        hasContent: true,
      },
      {
        label: 'Upload Confirmation Photo',
        buyerCompleted: offerData.paymentStatus?.buyerPaid && offerData.paymentStatus?.sellerPaid,
        sellerCompleted: offerData.shippingStatus?.confirmationPhoto ? true : false,
        hasContent: true,
      },
      {
        label: 'Generate Shipping Label',
        buyerCompleted: offerData.shippingStatus?.inTransit || offerData.shippingStatus?.delivered || false,
        sellerCompleted: offerData.shippingStatus?.inTransit || offerData.shippingStatus?.delivered || false,
        hasContent: true,
      },
      {
        // Completes on delivery (carrier or the buyer's "Mark as Received"):
        // the former separate "Marked as Delivered" step folded in, 2026-11-01.
        label: 'Item In Transit',
        buyerCompleted: offerData.shippingStatus?.delivered || false,
        sellerCompleted: offerData.shippingStatus?.delivered || false,
        hasContent: true,
      }
    ];
    
    mainSteps.forEach((step, idx) => {
      const stepCompleted = step.buyerCompleted && step.sellerCompleted;
      // Collapse step if both users have completed it
      // EXCEPT: Don't collapse Feedback step when delivered (auto-expand it)
      // EXCEPT: Don't collapse Payment step when escrow countdown is active (auto-expand it)
      const isPaymentWithEscrowCountdown = step.label === 'Completed Payment' && 
                                          offerData.shippingStatus?.delivered && 
                                          offerData.transferStatus?.itemDeliveredAt;
      const isFeedbackWhenDelivered = step.label === 'Feedback Provided' && offerData.shippingStatus?.delivered;
      
      if (stepCompleted && step.label !== 'Feedback Provided' && step.label !== 'Completed Payment') {
        initialCollapsedState[idx] = true;
      }
      
      // Auto-expand payment when escrow countdown starts
      if (isPaymentWithEscrowCountdown) {
        initialCollapsedState[idx] = false;
      }
      
      // Auto-expand feedback when delivered
      if (isFeedbackWhenDelivered) {
        initialCollapsedState[idx] = false;
      }
    });
    setCollapsedSteps(initialCollapsedState);
  }, [offerData, currentUser]);

  const handleConfirmationPhotoUpload = async (file) => {
    if (!file) return;
    
    // 🔒 SECURITY: Check if seller has completed payment first
    const sellerPaid = offerData.paymentStatus?.sellerPaid || offerData.sellerPaymentStatus?.paid || false;
    
    if (!sellerPaid) {
      alert('⚠️ You must complete your payment before uploading a confirmation photo.');
      return;
    }

    // ✅ FILE TYPE VALIDATION: Restrict to specific image formats
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff', 'image/svg+xml'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.tiff', '.svg'];
    
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Please upload a valid image file (.jpg, .jpeg, .png, .tiff, .svg)');
      return;
    }

    // ✅ FILE SIZE VALIDATION: 10MB max
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert('File size must be less than 10MB. Please choose a smaller image.');
      return;
    }
    
    setUploading(true);
    try {
      // 🗜️ COMPRESSION: Compress before upload if > 1.5MB
      const originalSizeMB = file.size / 1024 / 1024;
      let fileToUpload = file;
      
      if (originalSizeMB > 1.5) {
        try {
          const compressionOptions = {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1800,
            useWebWorker: false,
            quality: 0.85,
            fileType: 'image/jpeg'
          };
          
          console.log(`🔄 Compressing confirmation photo (${originalSizeMB.toFixed(2)}MB)...`);
          fileToUpload = await imageCompression(file, compressionOptions);
          const compressedSizeMB = fileToUpload.size / 1024 / 1024;
          const savings = ((1 - fileToUpload.size / file.size) * 100).toFixed(1);
          console.log(`✅ Confirmation photo compressed: ${compressedSizeMB.toFixed(2)}MB (${savings}% smaller)`);
        } catch (compressionError) {
          console.warn('⚠️ Confirmation photo compression failed, uploading original:', compressionError);
          // Continue with original if compression fails — never block the upload
        }
      }

      const storage = getStorage();
      const storageRef = ref(storage, `confirmation-photos/${offerId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, fileToUpload);
      const downloadURL = await getDownloadURL(storageRef);
      
      await updateDoc(doc(db, 'purchaseOffers', offerId), {
        'shippingStatus.confirmationPhoto': downloadURL,
        'shippingStatus.confirmationPhotoUploadedAt': new Date()
      });
      
      // Update step timestamp for photo upload
      await updatePurchaseStepTimestamp('photo');
      
      await refreshPurchaseData();
    } catch (error) {
      console.error('Error uploading confirmation photo:', error);
      alert('Failed to upload confirmation photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleMarkDelivered = async () => {
    try {
      // Confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to mark this package as RECEIVED?\n\nOnly confirm this if you have physically received the pedal.`
      );
      
      if (!confirmed) {
        return; // User cancelled
      }

      setMarkingDelivered(true);
      
      console.log('🚀 Starting mark as delivered process...');
      console.log('📋 Purchase ID:', offerId);
      console.log('📋 Current user ID:', userId);
      
      // Update the purchase document to mark as delivered
      // The backend trigger (monitorPurchaseDelivery) will handle completion automatically
      console.log('📝 Updating Firestore document...');
      await updateDoc(doc(db, 'purchaseOffers', offerId), {
        'shippingStatus.delivered': true,
        'shippingStatus.deliveredAt': serverTimestamp()
      });
      console.log('✅ Firestore update successful');
      
      // Update step timestamp for delivery
      await updatePurchaseStepTimestamp('delivered');
      
      console.log('✅ Marked as delivered, backend trigger will process completion...');
      
      // Wait a moment for the backend trigger to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Refetch the purchase data
      await refreshPurchaseData();
      
      // Wait another moment and refetch again to ensure we catch any backend updates
      await new Promise(resolve => setTimeout(resolve, 2000));
      await refreshPurchaseData();
      
      // Show success message
      alert('Package marked as delivered successfully! The 24-hour escrow timer has started.');
      
    } catch (error) {
      console.error('Error marking as delivered:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        name: error.name
      });
      
      // Check if it's an App Check error
      if (error.message && error.message.includes('app-check')) {
        alert('App Check error detected. Please refresh the page and try again.');
      } else {
        alert(`Failed to mark package as delivered: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setMarkingDelivered(false);
    }
  };

  const handleReportIssue = async () => {
    openContact({
      category: 'report_purchase',
      subject: `Purchase Issue Report - Purchase ${offerId}`,
      message: `Please describe the issue with this purchase:\n\n`,
      context: {
        purchaseId: offerId,
        buyer: buyerUsername,
        seller: sellerUsername,
        timestamp: new Date().toLocaleString()
      }
    }, async () => {
      try {
        const offerRef = doc(db, 'purchaseOffers', offerId);
        await updateDoc(offerRef, {
          disputeHold: true,
          disputeHoldAt: serverTimestamp(),
          disputeHoldBy: currentUser?.uid,
          disputeHoldReason: 'User reported issue'
        });

        await addDoc(collection(db, 'issueReports'), {
          purchaseId: offerId,
          issueType: 'Reported via contact form',
          reportedBy: currentUser?.uid,
          reportedAt: new Date(),
          buyerUsername,
          sellerUsername,
          disputeHoldPlaced: true
        });

        console.log('Dispute hold placed and issue report logged');
      } catch (err) {
        console.error('Failed to place dispute hold:', err);
      }
    });
  };

  const handleCompletePayment = () => {
    // Navigate to purchase confirm page to complete payment
    navigate(`/purchase-confirm/${offerId}`);
  };

  const purchaseWrapperStyle = { backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' };

  // Auth still resolving (e.g. right after a pull-to-refresh reload) — show a
  // neutral loading state instead of prematurely deciding login/not-found.
  if (!authChecked) {
    return (
      <div className="p-4" style={purchaseWrapperStyle}>
        <div className="text-center">Loading purchase progress...</div>
      </div>
    );
  }

  if (!currentUser) {
    // Auth resolved to logged-out: prompt login (save URL to return after).
    sessionStorage.setItem('returnAfterLogin', window.location.pathname);
    return (
      <div className="p-4" style={purchaseWrapperStyle}>
        <div className="text-center text-red-600">
          Please log in to view this purchase.{' '}
          <a href="/login" style={{ color: '#2563eb', textDecoration: 'underline' }}>
            Click here to log in
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4" style={purchaseWrapperStyle}>
        <div className="text-center">Loading purchase progress...</div>
      </div>
    );
  }

  // Only after a completed fetch with a real user do we surface "not found".
  if (notFound) {
    return (
      <div className="p-4" style={purchaseWrapperStyle}>
        <div className="text-center text-gray-600">
          We couldn&apos;t find this purchase. It may have been removed, or you may not have access to it.
        </div>
      </div>
    );
  }

  // Data still settling: if the auth-triggered refresh is still in flight, keep
  // showing loading (avoids a not-found flash). Once all fetches have finished
  // with no data (no access / removed listing), show the friendly terminal state
  // instead of spinning forever.
  if (!offerData || !targetPedal) {
    if (refreshing) {
      return (
        <div className="p-4" style={purchaseWrapperStyle}>
          <div className="text-center">Loading purchase progress...</div>
        </div>
      );
    }
    return (
      <div className="p-4" style={purchaseWrapperStyle}>
        <div className="text-center text-gray-600">
          We couldn&apos;t find this purchase. It may have been removed, or you may not have access to it.
        </div>
      </div>
    );
  }

  const userId = currentUser?.uid;
  const isBuyer = userId === offerData.initiatorId;
  const isSeller = userId === offerData.responderId;
  const sellerHasPaid = offerData.paymentStatus?.sellerPaid || offerData.sellerPaymentStatus?.paid || false;

  // Debug payment status
  console.log('🔍 Purchase Progress Debug:', {
    buyerPaid: offerData.paymentStatus?.buyerPaid,
    sellerPaid: offerData.paymentStatus?.sellerPaid,
    currentUser: userId,
    isBuyer: isBuyer,
    isSeller: isSeller,
    initiatorId: offerData.initiatorId,
    responderId: offerData.responderId,
    transferStatus: offerData.transferStatus,
    shippingStatus: offerData.shippingStatus,
    delivered: offerData.shippingStatus?.delivered
  });

  // 📢 Share NPD (New Pedal Day) to Reddit
  const handleShareToReddit = () => {
    if (!targetPedal) {
      console.error('No target pedal data');
      return;
    }
    const pedalName = `${targetPedal.make || ''} ${targetPedal.model || ''}`.trim() || 'Pedal';
    const title = `NPD: ${pedalName}`;
    const postBody = `Just got this ${pedalName} from PedalxChange.com!\n\nCheck out PedalxChange for buying and trading guitar pedals: https://pedalxchange.com`;
    
    // Copy body text to clipboard as fallback
    navigator.clipboard.writeText(postBody).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    alert('Your post body has been copied to your clipboard! You can paste it into the text field on Reddit.');
    
    const redditUrl = `https://www.reddit.com/r/guitarpedals/submit?type=self&title=${encodeURIComponent(title)}&selftext=${encodeURIComponent(postBody)}`;
    const newWindow = window.open(redditUrl, '_blank');
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = redditUrl;
    }
  };

  // 📢 Share NPD to Facebook
  const handleShareToFacebook = () => {
    if (!targetPedal) {
      console.error('No target pedal data');
      return;
    }
    const pedalName = `${targetPedal.make || ''} ${targetPedal.model || ''}`.trim() || 'Pedal';
    const postText = `NPD: ${pedalName}! Just got this ${pedalName} from PedalxChange.com!`;
    const shareUrl = 'https://pedalxchange.com';
    
    // Copy text to clipboard since Facebook doesn't allow pre-filling post text
    navigator.clipboard.writeText(postText).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    alert('Your "New Pedal Day" message has been copied to your clipboard! You can paste it into your Facebook post.');
    
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const newWindow = window.open(fbUrl, '_blank', 'width=600,height=400');
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = fbUrl;
    }
  };

  // Define steps for purchase progress (matching TradeProgress structure)
  const steps = [
    {
      label: 'Completed Payment',
      buyerCompleted: offerData.paymentStatus?.buyerPaid || false,
      sellerCompleted: offerData.paymentStatus?.sellerPaid || false,
      hasContent: true,
      // Debug logging
      debug: () => {
        console.log('🔍 Payment Status Debug:', {
          buyerPaid: offerData.paymentStatus?.buyerPaid,
          sellerPaid: offerData.paymentStatus?.sellerPaid,
          currentUser: userId,
          isBuyer: isBuyer,
          isSeller: isSeller
        });
      },
      content: (
        <div style={{
          backgroundColor: '#F7F3EB',
          padding: '1rem',
          border: '18px solid #ffffff',
          borderRadius: '0px'
        }}>
          <div style={{ maxWidth: '768px', margin: '0 auto' }}>
            {/* Desktop: 3-column grid | Mobile: Center content on top, then users side-by-side */}
            <div 
              className="payment-layout"
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 2fr 1fr', 
                gap: '2rem',
                alignItems: 'start'
              }}
            >
              <style jsx>{`
                @media (max-width: 768px) {
                  .payment-layout {
                    display: block !important;
                  }
                  .payment-center-mobile {
                    display: block !important;
                    text-align: center !important;
                    margin-bottom: 1rem !important;
                  }
                  .payment-center-desktop {
                    display: none !important;
                  }
                  .payment-users-mobile {
                    display: grid !important;
                    grid-template-columns: 1fr 1fr !important;
                    gap: 1rem !important;
                  }
                  .payment-users-desktop {
                    display: none !important;
                  }
                }
                @media (min-width: 769px) {
                  .payment-center-mobile {
                    display: none !important;
                  }
                  .payment-users-mobile {
                    display: none !important;
                  }
                }
              `}</style>

              {/* Desktop Layout: Buyer Column */}
              <div className="payment-users-desktop" style={{ textAlign: 'center' }}>
                <h5 className="font-medium mb-3">{buyerUsername}'s Payment Status</h5>
                <div className="space-y-2 text-sm" style={{ marginBottom: '8px' }}>
                  <div>Payment: {offerData.paymentStatus?.buyerPaid ? <span style={{ whiteSpace: 'nowrap' }}><TealCheck />Completed</span> : 'Pending'}</div>
                </div>
                {isBuyer && !offerData.paymentStatus?.buyerPaid && (
                  <div style={{ marginTop: '24px', display: 'block' }}>
                  <button
                    onClick={handleCompletePayment}
                    style={{
                      backgroundColor: '#C9A227',
                      borderRadius: '9999px',
                      border: 'none',
                      padding: '6px 14px',
                      fontFamily: 'Nunito, sans-serif',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '15px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                    }}
                  >
                    Complete Payment
                  </button>
                  </div>
                )}
              </div>

              {/* Desktop Layout: Center Column */}
              <div className="payment-center-desktop" style={{ textAlign: 'center' }}>
                <h4 className="font-semibold text-lg mb-2">Payment & Payout Status</h4>
                <p className="text-sm text-gray-600 mb-1">
                  Track payment completion and payout transfer for this purchase.
                </p>
                {offerData.paymentStatus?.buyerPaid && getPurchasePrice(offerData) != null && (
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Purchase price: ${getPurchasePrice(offerData)}
                  </div>
                )}
                {offerData.transferStatus && (
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    {formatPurchaseTransferStatus(offerData)}
                  </div>
                )}
                {offerData.transferStatus?.status === 'held_in_escrow' && (
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '12px' }}>
                    <div style={{ fontWeight: 'bold', color: '#92400e' }}>Escrow Protection</div>
                    {offerData.shippingStatus?.delivered && (offerData.transferStatus?.itemDeliveredAt || offerData.shippingStatus?.deliveredAt) ? (
                      <div style={{ color: '#92400e', marginTop: '4px' }}>
                        24-hour escrow countdown has started
                        <div style={{ fontSize: '11px', marginTop: '4px' }}>
                          {(() => {
                            // Use itemDeliveredAt if available, otherwise fall back to shippingStatus.deliveredAt
                            const deliveredAt = offerData.transferStatus?.itemDeliveredAt 
                              ? offerData.transferStatus.itemDeliveredAt.toDate()
                              : offerData.shippingStatus.deliveredAt.toDate();
                            const releaseTime = new Date(deliveredAt.getTime() + 24 * 60 * 60 * 1000);
                            const now = new Date();
                            const remaining = releaseTime - now;
                            if (remaining <= 0) return 'Funds will be released shortly';
                            const hours = Math.floor(remaining / (60 * 60 * 1000));
                            const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
                            return `Funds will be released in approximately ${hours}h ${minutes}m`;
                          })()}
                        </div>
                      </div>
                    ) : isZeroNetEscrowHold(offerData) ? (
                      <div style={{ color: '#92400e' }}>
                        Sale price was fully applied to fees, so there is no cash payout. Delivery still needs confirmation before this purchase can close.
                      </div>
                    ) : (
                      <div style={{ color: '#92400e' }}>
                        Funds are held securely and released 24 hours after delivery is confirmed.
                      </div>
                    )}
                    {offerData.transferStatus.escrowReason && (
                      <div style={{ color: '#92400e', marginTop: '4px' }}>
                        Reason: {offerData.transferStatus.escrowReason}
                      </div>
                    )}
                  </div>
                )}
                {offerData.transferStatus && (offerData.transferStatus.status === 'processing' || offerData.transferStatus.status === 'held_in_escrow' || offerData.transferStatus.status === 'failed') && (
                  <button
                    onClick={handleRefreshStatus}
                    className="mt-2 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium transition-colors"
                    disabled={refreshing}
                  >
                    {refreshing ? 'Checking...' : 'Check Status'}
                  </button>
                )}
              </div>

              {/* Desktop Layout: Seller Column */}
              <div className="payment-users-desktop" style={{ textAlign: 'center' }}>
                <h5 className="font-medium mb-3">{sellerUsername}'s Payment Status</h5>
                <div className="space-y-2 text-sm" style={{ marginBottom: '8px' }}>
                  <div>Payment: {offerData.paymentStatus?.sellerPaid ? <span style={{ whiteSpace: 'nowrap' }}><TealCheck />Confirmed</span> : 'Pending'}</div>
                  {offerData.transferStatus && (
                    <div>Payout: {offerData.transferStatus.status === 'success' ? <span style={{ whiteSpace: 'nowrap' }}><TealCheck />Received</span> : 'Pending'}</div>
                  )}
                </div>
                {isSeller && !offerData.paymentStatus?.sellerPaid && (
                  <div style={{ marginTop: '24px', display: 'block' }}>
                  <button
                    onClick={() => navigate(`/seller-purchase-confirm/${offerId}`)}
                    style={{
                      backgroundColor: '#C9A227',
                      borderRadius: '9999px',
                      border: 'none',
                      padding: '6px 14px',
                      fontFamily: 'Nunito, sans-serif',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '15px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                    }}
                  >
                    Complete Payment
                  </button>
                  </div>
                )}
              </div>

              {/* Mobile Layout: Center content at top */}
              <div className="payment-center-mobile">
                <div>
                  <h4 className="font-semibold text-lg mb-2">Payment & Payout Status</h4>
                  <p className="text-sm text-gray-600 mb-1">
                    Track payment completion and payout transfer for this purchase.
                  </p>
                  {offerData.paymentStatus?.buyerPaid && getPurchasePrice(offerData) != null && (
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Purchase price: ${getPurchasePrice(offerData)}
                    </div>
                  )}
                  {offerData.transferStatus && (
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                      {formatPurchaseTransferStatus(offerData)}
                    </div>
                  )}
                  {offerData.transferStatus?.status === 'held_in_escrow' && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '12px' }}>
                      <div style={{ fontWeight: 'bold', color: '#92400e' }}>Escrow Protection</div>
                      {offerData.shippingStatus?.delivered && (offerData.transferStatus?.itemDeliveredAt || offerData.shippingStatus?.deliveredAt) ? (
                        <div style={{ color: '#92400e', marginTop: '4px' }}>
                          24-hour escrow countdown has started
                          <div style={{ fontSize: '11px', marginTop: '4px' }}>
                            {(() => {
                              // Use itemDeliveredAt if available, otherwise fall back to shippingStatus.deliveredAt
                              const deliveredAt = offerData.transferStatus?.itemDeliveredAt 
                                ? offerData.transferStatus.itemDeliveredAt.toDate()
                                : offerData.shippingStatus.deliveredAt.toDate();
                              const releaseTime = new Date(deliveredAt.getTime() + 24 * 60 * 60 * 1000);
                              const now = new Date();
                              const remaining = releaseTime - now;
                              if (remaining <= 0) return 'Funds will be released shortly';
                              const hours = Math.floor(remaining / (60 * 60 * 1000));
                              const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
                              return `Funds will be released in approximately ${hours}h ${minutes}m`;
                            })()}
                          </div>
                        </div>
                      ) : isZeroNetEscrowHold(offerData) ? (
                        <div style={{ color: '#92400e' }}>
                          Sale price was fully applied to fees, so there is no cash payout. Delivery still needs confirmation before this purchase can close.
                        </div>
                      ) : (
                        <div style={{ color: '#92400e' }}>
                          Funds are held securely and released 24 hours after delivery is confirmed.
                        </div>
                      )}
                      {offerData.transferStatus.escrowReason && (
                        <div style={{ color: '#92400e', marginTop: '4px' }}>
                          Reason: {offerData.transferStatus.escrowReason}
                        </div>
                      )}
                    </div>
                  )}
                  {offerData.transferStatus && (offerData.transferStatus.status === 'processing' || offerData.transferStatus.status === 'held_in_escrow' || offerData.transferStatus.status === 'failed') && (
                    <button
                      onClick={handleRefreshStatus}
                      className="mt-2 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium transition-colors"
                      disabled={refreshing}
                    >
                      {refreshing ? 'Checking...' : 'Check Status'}
                    </button>
                  )}
                </div>
              </div>

              {/* Mobile Layout: Users side-by-side below */}
              <div className="payment-users-mobile">
                {/* Buyer Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{buyerUsername}'s Payment Status</h5>
                  <div className="space-y-2 text-sm" style={{ marginBottom: '8px', textAlign: 'center' }}>
                    <div>Payment: {offerData.paymentStatus?.buyerPaid ? <span style={{ whiteSpace: 'nowrap' }}><TealCheck />Completed</span> : 'Pending'}</div>
                  </div>
                  {isBuyer && !offerData.paymentStatus?.buyerPaid && (
                    <div style={{ marginTop: '24px', display: 'block' }}>
                    <button
                      onClick={handleCompletePayment}
                      style={{
                        backgroundColor: '#C9A227',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: '6px 14px',
                        fontFamily: 'Nunito, sans-serif',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '15px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                      }}
                    >
                      Complete Payment
                    </button>
                    </div>
                  )}
                </div>

                {/* Seller Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{sellerUsername}'s Payment Status</h5>
                  <div className="space-y-2 text-sm" style={{ marginBottom: '8px', textAlign: 'center' }}>
                    <div>Payment: {offerData.paymentStatus?.sellerPaid ? <span style={{ whiteSpace: 'nowrap' }}><TealCheck />Confirmed</span> : 'Pending'}</div>
                    {offerData.transferStatus && (
                      <div>Payout: {offerData.transferStatus.status === 'success' ? <span style={{ whiteSpace: 'nowrap' }}><TealCheck />Received</span> : 'Pending'}</div>
                    )}
                  </div>
                  {isSeller && !offerData.paymentStatus?.sellerPaid && (
                    <div style={{ marginTop: '24px', display: 'block' }}>
                    <button
                      onClick={() => navigate(`/seller-purchase-confirm/${offerId}`)}
                      style={{
                        backgroundColor: '#C9A227',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: '6px 14px',
                        fontFamily: 'Nunito, sans-serif',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '15px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                      }}
                    >
                      Complete Payment
                    </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'Upload Confirmation Photo',
      buyerCompleted: offerData.paymentStatus?.buyerPaid && offerData.paymentStatus?.sellerPaid,
      sellerCompleted: offerData.shippingStatus?.confirmationPhoto || false,
      hasContent: true,
      content: (
        <div style={{
          backgroundColor: '#F7F3EB',
          padding: '1rem',
          border: '18px solid #ffffff',
          borderRadius: '0px'
        }}>
          <div style={{ maxWidth: '768px', margin: '0 auto' }}>
            {/* Desktop: 3-column grid | Mobile: Center content on top, then users side-by-side */}
            <div 
              className="photo-layout"
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 2fr 1fr', 
                gap: '2rem',
                alignItems: 'start'
              }}
            >
              <style jsx>{`
                @media (max-width: 768px) {
                  .photo-layout {
                    display: block !important;
                  }
                  .photo-center-mobile {
                    display: block !important;
                    text-align: center !important;
                    margin-bottom: 1rem !important;
                  }
                  .photo-center-desktop {
                    display: none !important;
                  }
                  .photo-users-mobile {
                    display: grid !important;
                    grid-template-columns: 1fr 1fr !important;
                    gap: 2rem !important;
                  }
                  .photo-users-desktop {
                    display: none !important;
                  }
                }
                @media (min-width: 769px) {
                  .photo-center-mobile {
                    display: none !important;
                  }
                  .photo-users-mobile {
                    display: none !important;
                  }
                }
              `}</style>

              {/* Desktop Layout: Buyer Column */}
              <div className="photo-users-desktop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{buyerUsername}</h5>
                <div className="text-sm text-gray-600">No Action Required</div>
              </div>

              {/* Desktop Layout: Center Column */}
              <div className="photo-center-desktop text-center" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div>
                  <h4 className="font-semibold text-lg mb-2">Upload Your Confirmation Photo</h4>
                  <p className="text-sm text-gray-600 mb-1">
                    Upload an image of the pedal being packaged, for verification purposes.
                  </p>
                  <img
                    className="confirmation-photo-example"
                    src="/images/confirmation-photo-example.png"
                    alt="Example: photograph your pedal packed in the open box with packing material"
                    style={{ width: '260px', maxWidth: '100%', margin: '12px auto', opacity: 0.85, mixBlendMode: 'multiply' }}
                  />
                  <p className="text-xs text-gray-500" style={{ fontSize: '0.625rem' }}>
                    Accepted formats: JPG, PNG, TIFF • Max size: 10MB
                  </p>
                </div>
              </div>

              {/* Desktop Layout: Seller Column */}
              <div className="photo-users-desktop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{sellerUsername}'s Photo</h5>
                {offerData.shippingStatus?.confirmationPhoto ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={offerData.shippingStatus.confirmationPhoto}
                      alt={`${sellerUsername}'s confirmation photo`}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ width: '140px', height: '140px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ccc' }}
                      onClick={() => window.open(offerData.shippingStatus.confirmationPhoto, '_blank')}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">Click to view full size</p>
                  </div>
                ) : isSeller ? (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg" style={{ textAlign: 'center', width: 'fit-content', margin: '0 auto' }}>
                    {!sellerHasPaid && (
                      <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                    )}
                    <input
                      id="confirmation-photo-input"
                      type="file"
                      accept=".jpg,.jpeg,.png,.tiff,.svg,image/jpeg,image/png,image/tiff,image/svg+xml"
                      onChange={(e) => handleConfirmationPhotoUpload(e.target.files[0])}
                      disabled={uploading || !sellerHasPaid}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => document.getElementById('confirmation-photo-input').click()}
                      disabled={uploading || !sellerHasPaid}
                      className="hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: '#C9A227',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: '6px 14px',
                        fontFamily: 'Nunito, sans-serif',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        cursor: (uploading || !sellerHasPaid) ? 'not-allowed' : 'pointer',
                        opacity: (uploading || !sellerHasPaid) ? 0.5 : 1
                      }}
                    >
                      Upload Photo
                    </button>
                    {uploading && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Uploading...</div>}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">Upload Required</div>
                )}
              </div>

              {/* Mobile Layout: Center content at top */}
              <div className="photo-center-mobile">
                <div>
                  <h4 className="font-semibold text-lg mb-2">Upload Your Confirmation Photo</h4>
                  <p className="text-sm text-gray-600 mb-1">
                    Upload an image of the pedal being packaged, for verification purposes.
                  </p>
                  <img
                    className="confirmation-photo-example"
                    src="/images/confirmation-photo-example.png"
                    alt="Example: photograph your pedal packed in the open box with packing material"
                    style={{ width: '260px', maxWidth: '100%', margin: '10px auto', opacity: 0.85, display: 'block', mixBlendMode: 'multiply' }}
                  />
                  <p className="text-xs text-gray-500" style={{ fontSize: '0.625rem' }}>
                    Accepted formats: JPG, PNG, TIFF • Max size: 10MB
                  </p>
                </div>
              </div>

              {/* Mobile Layout: Users side-by-side below */}
              <div className="photo-users-mobile">
                {/* Buyer Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{buyerUsername}</h5>
                  <div className="text-sm text-gray-600">No Action Required</div>
                </div>

                {/* Seller Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{sellerUsername}'s Photo</h5>
                  {offerData.shippingStatus?.confirmationPhoto ? (
                    <div className="flex flex-col items-center">
                      <img
                        src={offerData.shippingStatus.confirmationPhoto}
                        alt={`${sellerUsername}'s confirmation photo`}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ccc' }}
                        onClick={() => window.open(offerData.shippingStatus.confirmationPhoto, '_blank')}
                      />
                      <p className="text-xs text-gray-500 mt-1 text-center">Click to view full size</p>
                    </div>
                  ) : isSeller ? (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg" style={{ textAlign: 'center', width: 'fit-content', margin: '0 auto' }}>
                      {!sellerHasPaid && (
                        <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                      )}
                      <input
                        id="confirmation-photo-input-mobile"
                        type="file"
                        accept=".jpg,.jpeg,.png,.tiff,.svg,image/jpeg,image/png,image/tiff,image/svg+xml"
                        onChange={(e) => handleConfirmationPhotoUpload(e.target.files[0])}
                        disabled={uploading || !sellerHasPaid}
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => document.getElementById('confirmation-photo-input-mobile').click()}
                        disabled={uploading || !sellerHasPaid}
                        className="hover:opacity-80 transition-opacity"
                        style={{
                          backgroundColor: '#C9A227',
                          borderRadius: '9999px',
                          border: 'none',
                          padding: '6px 14px',
                          fontFamily: 'Nunito, sans-serif',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '15px',
                          cursor: (uploading || !sellerHasPaid) ? 'not-allowed' : 'pointer',
                          opacity: (uploading || !sellerHasPaid) ? 0.5 : 1
                        }}
                      >
                        Upload Photo
                      </button>
                      {uploading && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Uploading...</div>}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">Upload Required</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'Generate Shipping Label',
      buyerCompleted: offerData.paymentStatus?.buyerPaid && offerData.paymentStatus?.sellerPaid,
      sellerCompleted: offerData.shippingStatus?.labelCreated || false,
      hasContent: true,
      content: (
        <div style={{
          backgroundColor: '#F7F3EB',
          padding: '1rem',
          border: '18px solid #ffffff',
          borderRadius: '0px'
        }}>
          <div style={{ maxWidth: '768px', margin: '0 auto' }}>
            {/* Desktop: 3-column grid | Mobile: Center content on top, then users side-by-side */}
            <div 
              className="shipping-layout"
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 2fr 1fr', 
                gap: '2rem',
                alignItems: 'start'
              }}
            >
              <style jsx>{`
                @media (max-width: 768px) {
                  .shipping-layout {
                    display: block !important;
                  }
                  .shipping-center-mobile {
                    display: block !important;
                    text-align: center !important;
                    margin-bottom: 1rem !important;
                  }
                  .shipping-center-desktop {
                    display: none !important;
                  }
                  .shipping-users-mobile {
                    display: grid !important;
                    grid-template-columns: 1fr 1fr !important;
                    gap: 2rem !important;
                  }
                  .shipping-users-desktop {
                    display: none !important;
                  }
                }
                @media (min-width: 769px) {
                  .shipping-center-mobile {
                    display: none !important;
                  }
                  .shipping-users-mobile {
                    display: none !important;
                  }
                }
              `}</style>

              {/* Desktop Layout: Buyer Column */}
              <div className="shipping-users-desktop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{buyerUsername}</h5>
                <div className="text-sm text-gray-600">No Action Required</div>
              </div>

              {/* Desktop Layout: Center Column */}
              <div className="shipping-center-desktop text-center" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <h4 className="font-semibold text-lg mb-2">Purchase Shipping Label</h4>
                <p className="text-sm text-gray-600">
                  Purchase your discounted, fully insured shipping label to send the pedal to the buyer.
                </p>
              </div>

              {/* Desktop Layout: Seller Column */}
              <div className="shipping-users-desktop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{sellerUsername}'s Shipping Label</h5>
                {offerData.shippingStatus?.labelCreated ? (
                  <div className="space-y-2" style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.875rem', color: '#2e7d6e', fontWeight: 600 }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      Label Created
                    </div>
                    {offerData.shippingStatus.labelUrl && (
                      <a 
                        href={offerData.shippingStatus.labelUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => { if (Capacitor.isNativePlatform()) { e.preventDefault(); openExternalUrl(offerData.shippingStatus.labelUrl); } }}
                        className="text-blue-600 hover:text-blue-800 text-sm underline block"
                      >
                        Download Label
                      </a>
                    )}
                    {offerData.shippingStatus.trackingNumber && (
                      <div className="text-xs text-gray-600">
                        <div><strong>Tracking:</strong></div>
                        <div style={{ fontSize: '11px', wordBreak: 'break-all', maxWidth: '200px' }}>{offerData.shippingStatus.trackingNumber}</div>
                      </div>
                    )}
                  </div>
                ) : isSeller && offerData.shippingStatus?.confirmationPhoto ? (
                  <button
                    onClick={handleOpenShippingForm}
                    className="hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: '#C9A227',
                      borderRadius: '9999px',
                      border: 'none',
                      padding: '6px 14px',
                      fontFamily: 'Nunito, sans-serif',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '15px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Purchase Label
                  </button>
                ) : (
                  <div className="text-sm text-gray-600">
                    {offerData.shippingStatus?.confirmationPhoto ? 'Label Required' : 'Photo Required First'}
                  </div>
                )}
              </div>

              {/* Mobile Layout: Center content at top */}
              <div className="shipping-center-mobile">
                <div>
                  <h4 className="font-semibold text-lg mb-2">Purchase Shipping Label</h4>
                  <p className="text-sm text-gray-600">
                    Purchase your discounted, fully insured shipping label to send the pedal to the buyer.
                  </p>
                </div>
              </div>

              {/* Mobile Layout: Users side-by-side below */}
              <div className="shipping-users-mobile">
                {/* Buyer Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{buyerUsername}</h5>
                  <div className="text-sm text-gray-600">No Action Required</div>
                </div>

                {/* Seller Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <h5 className="font-medium mb-3" style={{ textAlign: 'center', width: '100%' }}>{sellerUsername}'s Label</h5>
                  {offerData.shippingStatus?.labelCreated ? (
                    <div className="space-y-2" style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.875rem', color: '#2e7d6e', fontWeight: 600 }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </div>
                        Label Created
                      </div>
                      {offerData.shippingStatus.labelUrl && (
                        <a 
                          href={offerData.shippingStatus.labelUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => { if (Capacitor.isNativePlatform()) { e.preventDefault(); openExternalUrl(offerData.shippingStatus.labelUrl); } }}
                          className="text-blue-600 hover:text-blue-800 text-sm underline block"
                        >
                          Download Label
                        </a>
                      )}
                      {offerData.shippingStatus.trackingNumber && (
                        <div className="text-xs text-gray-600">
                          <div><strong>Tracking:</strong></div>
                          <div style={{ fontSize: '11px', wordBreak: 'break-all' }}>{offerData.shippingStatus.trackingNumber}</div>
                        </div>
                      )}
                    </div>
                  ) : isSeller && offerData.shippingStatus?.confirmationPhoto ? (
                    <button
                      onClick={handleOpenShippingForm}
                      className="hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: '#C9A227',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: '6px 14px',
                        fontFamily: 'Nunito, sans-serif',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Purchase Label
                    </button>
                  ) : (
                    <div className="text-sm text-gray-600">
                      {offerData.shippingStatus?.confirmationPhoto ? 'Label Required' : 'Photo Required First'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Shipping Form - Show when showShippingForm is true */}
            {showShippingForm && (
              <div style={{ marginTop: '2rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontFamily: 'Nunito, sans-serif', margin: 0, marginBottom: '0.5rem' }}>Purchase Shipping Label</h3>
                  <button
                    onClick={() => setShowShippingForm(false)}
                    style={{
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '0.25rem 0.5rem',
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Package Dimensions Form */}
                {shippingRates.length === 0 && (
                  <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                    <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '0.5rem' }}>Package Dimensions</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Length (inches)</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={packageData.length}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                              setPackageData(prev => ({ ...prev, length: value }));
                            }
                          }}
                          placeholder="0.0"
                          style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Width (inches)</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={packageData.width}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                              setPackageData(prev => ({ ...prev, width: value }));
                            }
                          }}
                          placeholder="0.0"
                          style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Height (inches)</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={packageData.height}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                              setPackageData(prev => ({ ...prev, height: value }));
                            }
                          }}
                          placeholder="0.0"
                          style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Weight (pounds)</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={packageData.weight}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                              setPackageData(prev => ({ ...prev, weight: value }));
                            }
                          }}
                          placeholder="0.0"
                          style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* From Address - Centered below Package Dimensions */}
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '0.5rem' }}>From Address</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                    {/* Row 1: Name, Address, Apt */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                      <input
                        type="text"
                        placeholder="Name"
                        value={packageData.fromAddress.name}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="Address"
                        value={packageData.fromAddress.street1}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="Apt"
                        value={packageData.fromAddress.street2}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                    </div>
                    {/* Row 2: City, State, Zip */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                      <input
                        type="text"
                        placeholder="City"
                        value={packageData.fromAddress.city}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={packageData.fromAddress.state}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="Zip Code"
                        value={packageData.fromAddress.zip}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                    </div>
                    {/* Row 3: Email, Phone */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                      <input
                        type="email"
                        placeholder="Email"
                        value={packageData.fromAddress.email}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="tel"
                        placeholder="Phone"
                        value={packageData.fromAddress.phone}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                    </div>
                  </div>
                </div>

                {/* To Address - Below From Address */}
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '0.5rem' }}>To Address</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                    {/* Row 1: Name, Address, Apt */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                      <input
                        type="text"
                        placeholder="Name"
                        value={packageData.toAddress.name}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="Address"
                        value={packageData.toAddress.street1}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="Apt"
                        value={packageData.toAddress.street2}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                    </div>
                    {/* Row 2: City, State, Zip */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                      <input
                        type="text"
                        placeholder="City"
                        value={packageData.toAddress.city}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={packageData.toAddress.state}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="text"
                        placeholder="Zip Code"
                        value={packageData.toAddress.zip}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                    </div>
                    {/* Row 3: Email, Phone */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                      <input
                        type="email"
                        placeholder="Email"
                        value={maskEmail(packageData.toAddress.email)}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                      <input
                        type="tel"
                        placeholder="Phone"
                        value={maskPhone(packageData.toAddress.phone)}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Get Rates Button */}
                <div style={{ marginTop: '1rem' }}>
                  <button
                    onClick={handleGetShippingRates}
                    disabled={gettingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                              !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                              !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                              !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                              !packageData.toAddress.state || !packageData.toAddress.zip}
                    style={{
                      backgroundColor: (gettingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                                       !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                                       !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                       !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                                       !packageData.toAddress.state || !packageData.toAddress.zip) ? '#E5E7EB' : '#C9A227',
                      color: 'white',
                      border: 'none',
                      borderRadius: '9999px',
                      padding: '6px 14px',
                      fontFamily: 'Nunito, sans-serif',
                      fontWeight: 'bold',
                      fontSize: '15px',
                      cursor: (gettingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                               !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                               !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                               !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                               !packageData.toAddress.state || !packageData.toAddress.zip) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {gettingRates ? 'Getting Rates...' : 'Get Shipping Rates'}
                  </button>
                </div>

                {/* Shipping Rates */}
                {shippingRates.length > 0 && (
                  <div>
                    <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '1rem' }}>Available Shipping Options</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                      {Object.entries(shippingRates.reduce((acc, rate) => {
                        const carrier = rate.provider || 'Unknown';
                        if (!acc[carrier]) acc[carrier] = [];
                        acc[carrier].push(rate);
                        return acc;
                      }, {})).map(([carrier, rates]) => (
                        <div key={carrier} style={{ 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '8px', 
                          padding: '1rem',
                          backgroundColor: '#f9fafb'
                        }}>
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ 
                              fontFamily: 'Nunito, sans-serif',
                              fontWeight: 'bold',
                              fontSize: '18px',
                              color: '#1f2937',
                              margin: '0'
                            }}>{carrier}</h5>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {rates.map((rate, index) => (
                              <div key={index} style={{ 
                                border: '1px solid #d1d5db', 
                                borderRadius: '8px', 
                                padding: '12px', 
                                backgroundColor: '#f9fafb'
                              }}>
                                <div style={{ marginBottom: '12px' }}>
                                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                                    {rate.servicelevel || 'Standard'}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                    {rate.estimated_days} day{rate.estimated_days !== 1 ? 's' : ''} delivery
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Total Cost:</span>
                                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#059669' }}>${rate.displayAmount}</span>
                                  </div>
                                    <div style={{ 
                                    fontSize: '11px', 
                                    color: '#059669', 
                                    backgroundColor: '#f0fdf4', 
                                    padding: '6px 8px', 
                                    borderRadius: '4px',
                                    border: '1px solid #bbf7d0'
                                    }}>
                                    ✓ Includes insurance
                                    </div>
                                </div>
                                <button
                                  onClick={() => handleCreateCheckout(rate)}
                                  disabled={creatingCheckout}
                                  style={{
                                    width: '100%',
                                    backgroundColor: creatingCheckout ? '#E5E7EB' : '#C9A227',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '9999px',
                                    padding: '6px 14px',
                                    fontFamily: 'Nunito, sans-serif',
                                    fontWeight: 'bold',
                                    fontSize: '15px',
                                    cursor: creatingCheckout ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {creatingCheckout ? 'Processing...' : 'Purchase Label'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      label: 'Item In Transit',
      buyerCompleted: offerData.shippingStatus?.delivered || false, // Don't collapse until delivered
      sellerCompleted: offerData.shippingStatus?.delivered || false, // Don't collapse until delivered
      hasContent: true,
      content: (
        <div style={{
          backgroundColor: '#F7F3EB',
          padding: '1rem',
          border: '18px solid #ffffff',
          borderRadius: '0px'
        }}>
          <div style={{ maxWidth: '768px', margin: '0 auto' }}>
          {/* Desktop Layout */}
          <div className="shipping-tracking-desktop" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Center Column */}
            <div className="shipping-center-desktop" style={{ textAlign: 'center', flex: '1' }}>
              <h4 className="font-semibold text-lg mb-2">Package Tracking</h4>
              {offerData.shippingStatus?.trackingNumber ? (
                <div>
                  <div style={{ fontSize: '14px', color: '#059669', fontWeight: 'bold', marginBottom: '8px' }}>
                    {offerData.shippingStatus?.inTransit ? 'In Transit' : 'Label Created'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Tracking: {offerData.shippingStatus.trackingNumber}
                  </div>
                  {offerData.shippingStatus.currentTrackingStatus && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.5rem' }}>
                      <strong>Status:</strong> {typeof offerData.shippingStatus.currentTrackingStatus === 'object' ? offerData.shippingStatus.currentTrackingStatus.status : offerData.shippingStatus.currentTrackingStatus}
                    </div>
                  )}
                  {(offerData.shippingStatus.lastUpdated || offerData.shippingStatus.lastTrackingUpdate) && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Last Updated: {new Date(((offerData.shippingStatus.lastUpdated || offerData.shippingStatus.lastTrackingUpdate).seconds) * 1000).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  Tracking information will appear here once the package is shipped.
                </div>
              )}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="shipping-tracking-mobile" style={{ display: 'none' }}>
            <div>
              <h4 className="font-semibold text-lg mb-2">Package Tracking</h4>
              {offerData.shippingStatus?.trackingNumber ? (
                <div>
                  <div style={{ fontSize: '14px', color: '#059669', fontWeight: 'bold', marginBottom: '8px' }}>
                    {offerData.shippingStatus?.inTransit ? 'In Transit' : 'Label Created'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Tracking: {offerData.shippingStatus.trackingNumber}
                  </div>
                  {offerData.shippingStatus.currentTrackingStatus && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.5rem' }}>
                      <strong>Status:</strong> {typeof offerData.shippingStatus.currentTrackingStatus === 'object' ? offerData.shippingStatus.currentTrackingStatus.status : offerData.shippingStatus.currentTrackingStatus}
                    </div>
                  )}
                  {(offerData.shippingStatus.lastUpdated || offerData.shippingStatus.lastTrackingUpdate) && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Last Updated: {new Date(((offerData.shippingStatus.lastUpdated || offerData.shippingStatus.lastTrackingUpdate).seconds) * 1000).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  Tracking information will appear here once the package is shipped.
                </div>
              )}
            </div>
          </div>

          {/* Tracking History */}
          {offerData.shippingStatus?.trackingHistory && offerData.shippingStatus.trackingHistory.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <details style={{ fontSize: '12px' }}>
                <summary style={{ cursor: 'pointer', color: '#5A9ECB', fontWeight: 'bold' }}>
                  View Tracking History ({offerData.shippingStatus.trackingHistory.filter(event => typeof event.status === 'string').length} events)
                </summary>
                <div style={{ marginTop: '8px', maxHeight: '256px', overflowY: 'auto', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '6px', maxWidth: '100%' }}>
                  {offerData.shippingStatus.trackingHistory.filter(event => typeof event.status === 'string').map((event, idx) => (
                    <div key={idx} style={{ marginBottom: '8px', padding: '10px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}>
                      {/* Date on top */}
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', fontWeight: '500' }}>
                        {event.statusDate ? new Date(event.statusDate).toLocaleDateString() : (event.timestamp ? new Date(event.timestamp).toLocaleDateString() : '')}
                      </div>
                      {/* Status and details */}
                      <div style={{ fontWeight: '600', fontSize: '11px', color: '#111827', textTransform: 'uppercase' }}>
                        {event.status || 'Unknown'}
                      </div>
                      {(event.statusDetail || event.status_details) && (
                        <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px' }}>
                          {event.statusDetail || event.status_details}
                        </div>
                      )}
                      {event.location && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                          📍 {event.location}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Refresh Tracking Button */}
          {offerData.shippingStatus?.trackingNumber && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                onClick={refreshPurchaseTrackingStatus}
                disabled={refreshing}
                style={{
                  backgroundColor: '#C9A227',
                  borderRadius: '9999px',
                  border: 'none',
                  padding: '8px 20px',
                  fontFamily: 'Nunito, sans-serif',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: refreshing ? 'not-allowed' : 'pointer',
                  opacity: refreshing ? 0.5 : 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Tracking Status'}
              </button>
            </div>
          )}

          {/* Mark as Received Button */}
          {(() => {
            const showButton = isBuyer && 
              offerData.status !== 'complete' && 
              !offerData.shippingStatus?.delivered &&
              offerData.paymentStatus?.buyerPaid && 
              offerData.paymentStatus?.sellerPaid && 
              offerData.shippingStatus?.trackingNumber &&
              offerData.shippingStatus?.inTransit;
            
            console.log('🔍 Mark as Received Button Check:', {
              isBuyer,
              status: offerData.status,
              delivered: offerData.shippingStatus?.delivered,
              buyerPaid: offerData.paymentStatus?.buyerPaid,
              sellerPaid: offerData.paymentStatus?.sellerPaid,
              trackingNumber: offerData.shippingStatus?.trackingNumber,
              inTransit: offerData.shippingStatus?.inTransit,
              showButton
            });
            
            return showButton;
          })() && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                onClick={handleMarkDelivered}
                disabled={markingDelivered}
                style={{
                  backgroundColor: markingDelivered ? '#9CA3AF' : '#C9A227',
                  borderRadius: '9999px',
                  border: 'none',
                  padding: '8px 16px',
                  fontFamily: 'Nunito, sans-serif',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: markingDelivered ? 'not-allowed' : 'pointer',
                  opacity: markingDelivered ? 0.6 : 1
                }}
                onMouseEnter={(e) => { if (!markingDelivered) e.target.style.opacity = '0.8'; }}
                onMouseLeave={(e) => { if (!markingDelivered) e.target.style.opacity = '1'; }}
              >
                {markingDelivered ? 'Marking Received...' : 'Mark as Received'}
              </button>
            </div>
          )}

          <style jsx>{`
            @media (max-width: 768px) {
              .shipping-tracking-desktop {
                display: none !important;
              }
              .shipping-tracking-mobile {
                display: block !important;
              }
            }
          `}</style>
          </div>
        </div>
      )
    },
    {
      label: 'Feedback Provided',
      buyerCompleted: !!offerData.buyerFeedback,
      sellerCompleted: !!offerData.sellerFeedback,
      hasContent: true,
      content: (
        <div style={{
          backgroundColor: '#F7F3EB',
          padding: '1rem',
          border: '18px solid #ffffff',
          borderRadius: '0px'
        }}>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          {/* See the note on the other FeedbackForm mount below: completion is
              the gate, not delivery. */}
          {(offerData.shippingStatus?.delivered || offerData.status === 'complete') ? (
            <FeedbackForm
              trade={offerData}
              tradeId={offerId}
              tradeType="purchase"
              currentUserId={userId}
              buyerId={offerData.initiatorId}
              sellerId={offerData.responderId}
              onFeedbackSubmitted={refreshPurchaseData}
              shareLinks={isBuyer ? (
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1.5rem'
                }}>
                  <button
                    onClick={handleShareToReddit}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#FF4500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Share on Reddit
                  </button>
                  <button
                    onClick={handleShareToFacebook}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#1877F2',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Share on Facebook
                  </button>
                </div>
              ) : undefined}
            />
          ) : (
            <p style={{ color: '#6b7280' }}>Feedback will be available after delivery confirmation.</p>
          )}
          </div>
        </div>
      )
    }
  ];

  // ---- Timeline redesign helpers (presentation only) ----
  const getInitial = (name) => ((name || '?').trim().charAt(0) || '?').toUpperCase();
  const getAvatarStyle = (side) =>
    side === 'buyer'
      ? { backgroundColor: '#e0edff', color: '#3b5bdb' }
      : { backgroundColor: '#efe3ff', color: '#7048e8' };

  const stepTitleMap = {
    'Completed Payment': 'Payment and payout',
    'Upload Confirmation Photo': 'Confirmation photo',
    'Generate Shipping Label': 'Shipping label',
    'Item In Transit': 'Item in transit',
    'Feedback Provided': 'Feedback',
  };

  const milestoneTotal = steps.length;
  const milestoneComplete = steps.filter((s) => s.buyerCompleted && s.sellerCompleted).length;

  const overallStatus = (() => {
    if (offerData.status === 'complete' && offerData.completedBy === 'admin') {
      return { label: 'Closed by support', tone: 'amber' };
    }
    if (offerData.status === 'complete') return { label: 'Complete', tone: 'green' };

    // First step both parties haven't finished — matches milestone count logic.
    for (const step of steps) {
      if (!step.buyerCompleted || !step.sellerCompleted) {
        const label = stepTitleMap[step.label] || step.label;
        const anyProgress = step.buyerCompleted || step.sellerCompleted;
        const tone = anyProgress ? 'blue' : (step.label === 'Completed Payment' ? 'amber' : 'blue');
        return { label, tone };
      }
    }
    return { label: 'Complete', tone: 'green' };
  })();

  const getStepSubtitle = (step, buyerDone, sellerDone) => {
    switch (step.label) {
      case 'Completed Payment':
        if (offerData.paymentStatus?.buyerPaid && offerData.paymentStatus?.sellerPaid) {
          if (offerData.transferStatus) {
            return offerData.transferStatus.status === 'success' ? 'Paid, payout complete' : 'Paid, payout pending';
          }
          return 'Payment complete';
        }
        return (offerData.paymentStatus?.buyerPaid || offerData.paymentStatus?.sellerPaid) ? 'Payment in progress' : 'Waiting for payment';
      case 'Upload Confirmation Photo':
        return offerData.shippingStatus?.confirmationPhoto ? 'Photo uploaded and verified' : 'Awaiting confirmation photo';
      case 'Generate Shipping Label':
        return offerData.shippingStatus?.labelCreated ? 'Shipping label purchased' : 'Purchase shipping label';
      case 'Item In Transit':
        if (offerData.shippingStatus?.delivered) return 'Delivered';
        if (offerData.shippingStatus?.inTransit) return 'Package in transit';
        return 'Awaiting shipment';
      case 'Feedback Provided':
        return (buyerDone && sellerDone) ? 'Feedback submitted' : 'Share your purchase experience';
      default:
        return '';
    }
  };

  return (
    <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      <h2 className="text-2xl font-bold mb-4">Purchase Progress</h2>

      {/* Responsive CSS for pedal card and report button */}
      <style>{`
        .pp-pedal-link {
          display: flex;
          align-items: center;
          gap: 6px;
          text-decoration: none;
          color: inherit;
        }
        .pp-pedal-thumb {
          width: 88px;
          height: 88px;
          border-radius: 10px;
          overflow: hidden;
          background-color: #f3f4f6;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pp-pedal-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .pp-amount-thumb {
          width: 88px;
          height: 88px;
          border-radius: 10px;
          background-color: #ecfdf5;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (min-width: 520px) {
          .pp-pedal-thumb,
          .pp-amount-thumb {
            width: 96px;
            height: 96px;
          }
        }
        .tp-trade-center {
          flex: 0 0 auto;
          text-align: center;
          align-self: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 0 8px;
        }
        .tp-trade-type {
          font-size: 12px;
          font-weight: 600;
          color: #059669;
          line-height: 1.3;
          text-align: center;
        }
        .tp-swap-badge {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #fafafa;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          flex-shrink: 0;
        }
        .tp-swap-badge svg {
          width: 30px;
          height: 30px;
        }
        .tp-tl-headbox-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: stretch;
          gap: 0.75rem;
          margin-top: 14px;
        }
        .tp-tl-action-btn {
          flex: 1 1 calc(50% - 0.375rem);
          min-width: 132px;
          max-width: 220px;
          padding: 0.75rem 1rem;
          border-radius: 9999px;
          border: 1px solid #d1d5db;
          background-color: #ffffff;
          color: #374151;
          font-size: 0.875rem;
          font-weight: 600;
          font-family: 'Nunito', sans-serif;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          text-decoration: none;
          cursor: pointer;
          box-sizing: border-box;
          line-height: 1.2;
          white-space: nowrap;
        }
        .tp-tl-action-btn:hover:not(:disabled) {
          background-color: #f9fafb;
        }
        .tp-tl-action-btn:disabled {
          background-color: #f3f4f6;
          color: #9ca3af;
          border-color: #e5e7eb;
          cursor: not-allowed;
        }
        .tp-tl-action-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #e57373;
          display: inline-block;
          flex-shrink: 0;
        }
        .tp-tl-action-dot.disabled {
          background-color: #9ca3af;
        }
        @media (max-width: 768px) {
          .pp-pedal-card,
          .pp-report-btn {
            width: calc(100% - 1rem) !important;
            margin-left: 0.5rem !important;
            margin-right: 0.5rem !important;
          }
        }
        @media (max-width: 480px) {
          .pp-head-col {
            align-items: center !important;
          }
          .pp-pedal-link {
            flex-direction: column !important;
            align-items: center !important;
            gap: 2px !important;
          }
          .pp-pedal-link .pp-pedal-text-beside {
            text-align: center !important;
            margin-top: 4px !important;
          }
          .pp-pedal-link .pp-pedal-text-beside div:first-child {
            font-size: 11px !important;
          }
          .pp-pedal-link .pp-pedal-text-beside div:last-child {
            font-size: 10px !important;
          }
        }
      `}</style>

      {/* Purchase Details Card */}
      <div style={{ maxWidth: '768px', width: '100%', margin: '0 auto', padding: '0 0.5rem', boxSizing: 'border-box' }}>
        <div className="tp-tl-headbox">
        {targetPedal && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '0',
        }}>
          {/* Payment Amount (Left Column) */}
          <div className="pp-head-col" style={{ flex: '1 1 35%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div className="pp-pedal-link">
              <div className="pp-amount-thumb">
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981', textAlign: 'center' }}>
                  ${offerData.acceptedAmount || offerData.offerAmount}
                </div>
              </div>
              <div className="pp-pedal-text-beside" style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937', fontFamily: 'Nunito', lineHeight: '1.3' }}>
                  {isSeller ? 'Sale Price' : 'Purchase Amount'}
                </div>
              </div>
            </div>
            {isSeller && (() => {
              const payoutCents = getSellerPayoutCents(offerData);
              if (payoutCents == null) return null;
              return (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', paddingLeft: '4px' }}>
                You receive: <span style={{ fontWeight: 'bold', color: '#10b981' }}>${(payoutCents / 100).toFixed(2)}</span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}> (after fees)</span>
              </div>
              );
            })()}
          </div>

          {/* Center Arrow */}
          <div className="tp-trade-center">
            <div className="tp-swap-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M9 8L5 12l4 4" />
                <path d="M15 16l4-4-4-4" />
              </svg>
            </div>
            <div className="tp-trade-type">Direct Purchase</div>
          </div>

          {/* Seller Pedal (Right Column) */}
          <div className="pp-head-col" style={{ flex: '1 1 35%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link
              to={`/archived-listing/purchase/${offerId}/target`}
              className="pp-pedal-link"
            >
              <div className="pp-pedal-thumb">
                {(targetPedal?.images?.top || placeholderFor(targetPedal || {}).url) ? (
                  <img src={targetPedal?.images?.top || placeholderFor(targetPedal || {}).url} alt={`${targetPedal.make} ${targetPedal.model}`} />
                ) : (
                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>No Image</span>
                )}
              </div>
              <div className="pp-pedal-text-beside" style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937', fontFamily: 'Nunito', lineHeight: '1.3' }}>{targetPedal.make} {targetPedal.model}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>${targetPedal.price || 0}</div>
              </div>
            </Link>
            {offerData
              && (offerData.sellerPaymentStatus?.paid || offerData.paymentStatus?.sellerPaid)
              && (typeof offerData.sellerPaymentStatus?.protectionSelected === 'boolean'
                    ? offerData.sellerPaymentStatus.protectionSelected
                    : offerData.protection?.seller === true) && (
              /* Shown only once the seller's fee payment is COMPLETE and it
                 actually included protection — the payment-time record
                 (sellerPaymentStatus.protectionSelected, what settlement
                 charges) wins over the mutable doc flag, which under the
                 2026-09-18 default-ON toggle means merely "selected".
                 Inline styles: the previous Tailwind centering classes are
                 inert in this project. */
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#1e40af', textAlign: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '12px' }}>Trade Protection Purchased</span>
              </div>
            )}
          </div>
        </div>
        )}

        <div className="tp-tl-divider" />

        <div className="tp-tl-topline">
          <span className="tp-tl-tradeid">Purchase #{(offerId || '').slice(-6).toUpperCase()}</span>
          <span className={`tp-badge tp-badge-${overallStatus.tone}`}>
            {overallStatus.tone === 'green' && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            )}
            {overallStatus.label}
          </span>
        </div>
        <div className="tp-tl-progress">
          <div style={{ width: `${milestoneTotal ? (milestoneComplete / milestoneTotal) * 100 : 0}%` }} />
        </div>
        <div className="tp-tl-progress-label">{milestoneComplete} of {milestoneTotal} milestones complete</div>

        {/* Messages + Report Issue */}
        <div className="tp-tl-headbox-actions">
          <Link to={`/purchase-messages/${offerId}`} className="tp-tl-action-btn">
            Messages
          </Link>
          <button
            onClick={handleReportIssue}
            disabled={offerData?.disputeHold === true}
            className="tp-tl-action-btn"
          >
            <span className={`tp-tl-action-dot${offerData?.disputeHold ? ' disabled' : ''}`} />
            {offerData?.disputeHold ? 'Issue reported' : 'Report an issue'}
          </button>
        </div>
        </div>
      </div>

      {/* Dispute Hold Banner */}
      {offerData?.disputeHold && (
        <div style={{
          maxWidth: '768px',
          margin: '0 auto 1rem auto',
          padding: '0.75rem 1rem',
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <span style={{ fontSize: '1.25rem' }}>⚠️</span>
          <div>
            <p style={{ margin: 0, fontWeight: '600', color: '#92400e', fontSize: '0.875rem' }}>
              Dispute In Progress
            </p>
            <p style={{ margin: 0, color: '#92400e', fontSize: '0.75rem' }}>
              An issue has been reported for this purchase. Escrow funds are on hold pending review by our support team.
            </p>
          </div>
        </div>
      )}

      <div className="tp-timeline" style={{ maxWidth: '768px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <style>{`
          .tp-timeline { padding: 0 0.5rem; font-family: 'Nunito', sans-serif; text-align: left; }
          .tp-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 9999px; font-size: 12px; font-weight: 700; white-space: nowrap; }
          .tp-badge-green { background: #dcfce7; color: #15803d; }
          .tp-badge-amber { background: #fdebc8; color: #b45309; }
          .tp-badge-rust { background: #f8e3d9; color: #ac4320; }
          .tp-badge-blue { background: #e0edff; color: #2f4fd4; }
          .tp-badge-gray { background: #eef1f5; color: #64748b; font-size: calc(12px + 1.5pt); }

          .tp-tl-topcard { background: #fff; border: 1px solid #eef0f2; border-radius: 16px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 18px; }
          .tp-tl-topline { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
          .tp-tl-tradeid { font-size: 0.95rem; font-weight: 600; color: #6b7280; }
          .tp-tl-users-row { display: flex; align-items: center; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
          .tp-tl-userchip { display: inline-flex; align-items: center; gap: 8px; font-weight: 700; color: #1f2937; font-size: 1rem; }
          .tp-tl-swap { color: #9ca3af; font-size: 1.2rem; }
          .tp-tl-avatar { width: 34px; height: 34px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; flex-shrink: 0; }
          .tp-tl-progress { height: 8px; background: #e5e7eb; border-radius: 9999px; overflow: hidden; margin-top: 14px; }
          .tp-tl-progress > div { height: 100%; background: #4db6ac; border-radius: 9999px; transition: width 0.3s ease; }
          .tp-tl-progress-label { font-size: 0.85rem; color: #6b7280; margin-top: 8px; }
          .tp-tl-headbox { background: #fff; border: 1px solid #eef0f2; border-radius: 16px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 18px; text-align: left; }
          .tp-tl-divider { height: 1px; background: #eef0f2; margin: 14px 0; }
          .tp-tl-headbox-actions { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 0.75rem; margin-top: 14px; }

          .tp-tl-list { }
          .tp-tl-row { display: flex; gap: 14px; }
          .tp-tl-row:not(:first-child) .tp-tl-header { padding-top: 14px; }
          .tp-tl-rail { display: flex; flex-direction: column; align-items: center; width: 26px; flex-shrink: 0; }
          .tp-tl-dot { width: 26px; height: 26px; border-radius: 50%; border: 2px solid #d1d5db; background: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
          .tp-tl-dot.partial { border-color: #9fd6cf; }
          .tp-tl-dot.done { border-color: #4db6ac; background: #4db6ac; }
          .tp-tl-line { flex: 1; width: 2px; background: #d1d5db; margin: 3px 0; min-height: 16px; }
          .tp-tl-line.done { background: #4db6ac; }
          .tp-tl-content { flex: 1; min-width: 0; padding-bottom: 38px; }
          .tp-tl-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; padding: 6px 0; }
          .tp-tl-title { font-size: 1.3rem; font-weight: 800; color: #111827; line-height: 1.2; }
          .tp-tl-sub { font-size: 0.9rem; color: #6b7280; margin-top: 3px; }
          .tp-tl-chevron { color: #9ca3af; font-size: 0.9rem; transition: transform 0.2s ease; flex-shrink: 0; margin-top: 6px; }

          .tp-tl-panel { background: #fff; border: 1px solid #eef0f2; border-radius: 14px; padding: 14px; margin-top: 8px; margin-left: -40px; position: relative; z-index: 1; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
          .tp-tl-help { font-size: calc(0.82rem + 1pt); font-weight: 700; color: #6b7280; line-height: 1.45; margin-bottom: 12px; }
          .tp-tl-help-note { display: block; font-size: 0.82rem; font-weight: 400; margin-top: 4px; }
          .tp-tl-help .accent { color: #C9A227; }
          .tp-tl-users { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
          .tp-tl-user { background: transparent; border: none; border-radius: 0; padding: 4px 16px; min-width: 0; }
          .tp-tl-users > .tp-tl-user:first-child { border-right: 1px solid #eef0f2; padding-right: 14px; }
          .tp-tl-users > .tp-tl-user:last-child { padding-left: 14px; }
          .tp-tl-user-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; min-width: 0; }
          .tp-tl-user-name { font-weight: 700; color: #1f2937; font-size: 0.95rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .tp-tl-line-item { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; font-size: 0.85rem; color: #374151; margin-bottom: 10px; }
          .tp-tl-line-item .lbl { display: inline-flex; align-items: center; gap: 5px; color: #4b5563; }
          .tp-tl-link { color: #5A9ECB; font-size: calc(0.82rem + 1pt); font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
          .tp-tl-link:hover { text-decoration: underline; }
          .tp-tl-mono {
            font-size: 0.68rem;
            color: #9ca3af;
            margin-top: 6px;
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            white-space: nowrap;
            overflow-x: auto;
            max-width: 100%;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .tp-tl-mono::-webkit-scrollbar { display: none; }
          .tp-tl-refresh { width: 100%; background: #fff; border: 1px solid #d1d5db; border-radius: 10px; padding: 10px 14px; font-family: 'Nunito', sans-serif; font-size: 0.95rem; font-weight: 600; color: #374151; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 12px; }
          .tp-tl-refresh:hover:not(:disabled) { background: #f9fafb; }
          .tp-tl-refresh:disabled { opacity: 0.5; cursor: not-allowed; }
          .tp-tl-photo-box { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 100%; }
          .tp-tl-photo-wrap { position: relative; display: inline-block; cursor: pointer; width: 100%; max-width: 160px; }
          .tp-tl-photo-wrap img { width: 100%; max-width: 160px; height: 110px; object-fit: cover; border-radius: 8px; display: block; }
          .tp-tl-photo-zoom {
            position: absolute;
            right: 6px;
            bottom: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #5F8FA0;
            pointer-events: none;
            filter: drop-shadow(0 1px 1px rgba(255,255,255,0.9));
          }
          .tp-tl-photo-zoom svg {
            width: 16px;
            height: 16px;
          }
          .tp-tl-photo-actions { display: flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap; }
          .tp-tl-gold-btn { background: #C9A227; border-radius: 9999px; border: none; padding: 10px 20px; font-family: 'Nunito', sans-serif; color: #fff; font-weight: 700; font-size: 0.95rem; cursor: pointer; text-align: center; line-height: 1.2; white-space: nowrap; display: inline-flex; align-items: center; justify-content: center; margin: 0; width: auto; max-width: 100%; }
          .tp-tl-gold-btn:disabled { opacity: 0.5; cursor: not-allowed; }
          a.tp-tl-gold-btn { text-decoration: none; color: #fff; }
          .tp-tl-gold-row { width: 100%; text-align: left; margin-top: 8px; margin-bottom: 10px; }
          .tp-tl-gold-row--mark-received { margin-top: 22px; }
          @media (max-width: 768px) {
            .tp-tl-users > .tp-tl-user:first-child { padding-right: 12px; padding-left: 0; }
            .tp-tl-users > .tp-tl-user:last-child { padding-left: 12px; padding-right: 0; }
            .tp-tl-user { padding: 4px 0; }
            .tp-tl-title { font-size: 1.15rem; }
            .tp-tl-user-name { font-size: 0.85rem; }
            .tp-tl-line-item { font-size: 0.8rem; }
            .tp-badge { padding: 2px 8px; font-size: 11px; }
            .tp-badge-gray { font-size: calc(11px + 1.5pt); }
          }
        `}</style>

        {/* Progress Steps */}
        <div className="tp-tl-list">
        {steps.map((step, idx) => {
          const buyerChecked = step.buyerCompleted;
          const sellerChecked = step.sellerCompleted;
          const isPaymentStep = step.label === 'Completed Payment';
          const isPhotoStep = step.label === 'Upload Confirmation Photo';
          const isLabelStep = step.label === 'Generate Shipping Label';
          const isTransitStep = step.label === 'Item In Transit';
          const isFeedbackStep = step.label === 'Feedback Provided';

          const stepCompleted = buyerChecked && sellerChecked;
          const hasContent = step.hasContent;
          const isCollapsed = collapsedSteps[idx];
          const canToggle = stepCompleted && hasContent;
          const isLastStep = idx === steps.length - 1;

          // Payout row data (payment step) — same pattern as TradeProgress so
          // the two pages read identically. Amount shows only to the seller
          // (the cash receiver), settled net via getSellerPayoutCents.
          const payoutStatus = getPayoutReleaseStatus(
            offerData.transferStatus,
            offerData.shippingStatus?.delivered
              ? (offerData.transferStatus?.itemDeliveredAt || offerData.shippingStatus?.deliveredAt)
              : null
          );
          const sellerPayoutCents = getSellerPayoutCents(offerData);
          const payoutAmountSuffix =
            isSeller && sellerPayoutCents > 0
              ? `: $${(sellerPayoutCents / 100).toFixed(2)}`
              : '';

          return (
            <div key={idx} className="tp-tl-row">
              <div className="tp-tl-rail">
                <div className={`tp-tl-dot${stepCompleted ? ' done' : (buyerChecked || sellerChecked ? ' partial' : '')}`}>
                  {stepCompleted && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </div>
                {!isLastStep && <div className={`tp-tl-line${stepCompleted ? ' done' : ''}`} />}
              </div>
              <div className="tp-tl-content">
                <div
                  className="tp-tl-header"
                  onClick={() => canToggle && toggleStepCollapse(idx)}
                  style={{ cursor: canToggle ? 'pointer' : 'default' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="tp-tl-title">{stepTitleMap[step.label] || step.label}</div>
                    <div className="tp-tl-sub">{getStepSubtitle(step, buyerChecked, sellerChecked)}</div>
                  </div>
                  {canToggle && (
                    <span className="tp-tl-chevron" style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>▾</span>
                  )}
                </div>

                {/* Payment and payout */}
                {isPaymentStep && !isCollapsed && (
                  <div className="tp-tl-panel">
                    {!stepCompleted && (
                      <div className="tp-tl-help">Track payment completion and payout transfer for this purchase.</div>
                    )}
                    {/* Escrow explainer — shared text above the user split,
                        matching TradeProgress word-for-word in style. Hidden
                        once the payout is released. */}
                    {payoutStatus.state !== 'released' && (
                      <div className="tp-tl-help" style={{ color: '#ac4320' }}>
                        Funds are held in escrow and released 24 hours after delivery is confirmed.
                      </div>
                    )}
                    <div className="tp-tl-users">
                      {/* Buyer card */}
                      <div className="tp-tl-user">
                        <div className="tp-tl-user-head">
                          <span className="tp-tl-avatar" style={getAvatarStyle('buyer')}>{getInitial(buyerUsername)}</span>
                          <span className="tp-tl-user-name">{buyerUsername}</span>
                        </div>
                        <div className="tp-tl-line-item">
                          <span className="lbl">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                            Payment
                          </span>
                          <span className={`tp-badge ${offerData.paymentStatus?.buyerPaid ? 'tp-badge-green' : 'tp-badge-amber'}`}>{offerData.paymentStatus?.buyerPaid ? 'Completed' : 'Pending'}</span>
                        </div>
                        {isBuyer && !offerData.paymentStatus?.buyerPaid && (
                          <div className="tp-tl-gold-row">
                            <button onClick={handleCompletePayment} className="tp-tl-gold-btn">Pay now</button>
                          </div>
                        )}
                      </div>
                      {/* Seller card */}
                      <div className="tp-tl-user">
                        <div className="tp-tl-user-head">
                          <span className="tp-tl-avatar" style={getAvatarStyle('seller')}>{getInitial(sellerUsername)}</span>
                          <span className="tp-tl-user-name">{sellerUsername}</span>
                        </div>
                        <div className="tp-tl-line-item">
                          <span className="lbl">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                            Payment
                          </span>
                          <span className={`tp-badge ${offerData.paymentStatus?.sellerPaid ? 'tp-badge-green' : 'tp-badge-amber'}`}>{offerData.paymentStatus?.sellerPaid ? 'Confirmed' : 'Pending'}</span>
                        </div>
                        <div className="tp-tl-line-item">
                          <span className="lbl">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                            Payout{payoutAmountSuffix}
                          </span>
                          <PayoutReleaseBadge
                            transferStatus={offerData.transferStatus}
                            deliveredAt={offerData.shippingStatus?.delivered
                              ? (offerData.transferStatus?.itemDeliveredAt || offerData.shippingStatus?.deliveredAt)
                              : null}
                          />
                        </div>
                        {isSeller && !offerData.paymentStatus?.sellerPaid && (
                          <div className="tp-tl-gold-row">
                            <button onClick={() => navigate(`/seller-purchase-confirm/${offerId}`)} className="tp-tl-gold-btn">Confirm</button>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                )}

                {/* Confirmation photo */}
                {isPhotoStep && !isCollapsed && (
                  <div className="tp-tl-panel">
                    {!stepCompleted && (
                      <div className="tp-tl-help">
                        Upload an image of the pedal being packaged, for verification purposes.
                        <span className="tp-tl-help-note">Accepted formats: JPG, PNG, TIFF • Max size: 10MB.</span>
                        <img
                          className="confirmation-photo-example"
                          src="/images/confirmation-photo-example.png"
                          alt="Example: photograph your pedal packed in the open box with packing material"
                          style={{ display: 'block', width: '260px', maxWidth: '100%', margin: '12px auto 0', opacity: 0.85, mixBlendMode: 'multiply' }}
                        />
                      </div>
                    )}
                    <div className="tp-tl-users">
                      {/* Buyer card */}
                      <div className="tp-tl-user">
                        <div className="tp-tl-user-head">
                          <span className="tp-tl-avatar" style={getAvatarStyle('buyer')}>{getInitial(buyerUsername)}</span>
                          <span className="tp-tl-user-name">{buyerUsername}</span>
                        </div>
                        <span className="tp-badge tp-badge-gray">No action needed</span>
                      </div>
                      {/* Seller card */}
                      <div className="tp-tl-user">
                        <div className="tp-tl-user-head">
                          <span className="tp-tl-avatar" style={getAvatarStyle('seller')}>{getInitial(sellerUsername)}</span>
                          <span className="tp-tl-user-name">{sellerUsername}</span>
                        </div>
                        {offerData.shippingStatus?.confirmationPhoto ? (
                          <div className="tp-tl-photo-box">
                            <div
                              className="tp-tl-photo-wrap"
                              onClick={() => openConfirmationPhoto(offerData.shippingStatus.confirmationPhoto, sellerUsername)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openConfirmationPhoto(offerData.shippingStatus.confirmationPhoto, sellerUsername); }}
                              aria-label="View confirmation photo full size"
                            >
                              <img
                                src={offerData.shippingStatus.confirmationPhoto}
                                alt={`${sellerUsername}'s confirmation photo`}
                              />
                              <span className="tp-tl-photo-zoom" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="11" cy="11" r="7" />
                                  <path d="M20 20l-3-3" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        ) : isSeller ? (
                          <div className="tp-tl-gold-row">
                            {!sellerHasPaid && (
                              <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                            )}
                            <input
                              id="confirmation-photo-input"
                              type="file"
                              accept=".jpg,.jpeg,.png,.tiff,.svg,image/jpeg,image/png,image/tiff,image/svg+xml"
                              onChange={(e) => handleConfirmationPhotoUpload(e.target.files[0])}
                              disabled={uploading || !sellerHasPaid}
                              style={{ display: 'none' }}
                            />
                            <button
                              onClick={() => document.getElementById('confirmation-photo-input').click()}
                              disabled={uploading || !sellerHasPaid}
                              className="tp-tl-gold-btn"
                            >
                              Upload photo
                            </button>
                            {uploading && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Uploading...</div>}
                          </div>
                        ) : (
                          <span className="tp-badge tp-badge-gray">Photo pending</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Shipping label */}
                {isLabelStep && !isCollapsed && (
                  <div className="tp-tl-panel">
                    {!stepCompleted && (
                      <div className="tp-tl-help">
                        Purchase your discounted, fully insured shipping label to send the pedal to the buyer.
                        <ShippingPricesLink onClick={() => setShowShippingPrices(true)} style={{ display: 'block', marginTop: '10px' }} />
                      </div>
                    )}
                    <div className="tp-tl-users">
                      {/* Buyer card */}
                      <div className="tp-tl-user">
                        <div className="tp-tl-user-head">
                          <span className="tp-tl-avatar" style={getAvatarStyle('buyer')}>{getInitial(buyerUsername)}</span>
                          <span className="tp-tl-user-name">{buyerUsername}</span>
                        </div>
                        <span className="tp-badge tp-badge-gray">No action needed</span>
                      </div>
                      {/* Seller card */}
                      <div className="tp-tl-user">
                        <div className="tp-tl-user-head">
                          <span className="tp-tl-avatar" style={getAvatarStyle('seller')}>{getInitial(sellerUsername)}</span>
                          <span className="tp-tl-user-name">{sellerUsername}</span>
                        </div>
                        {offerData.shippingStatus?.labelCreated ? (
                          <>
                            <div className="tp-tl-line-item">
                              <span className="lbl">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                                Label
                              </span>
                              <span className="tp-badge tp-badge-green">Purchased</span>
                            </div>
                            {offerData.shippingStatus.labelUrl && isSeller && (
                              <a
                                href={offerData.shippingStatus.labelUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { if (Capacitor.isNativePlatform()) { e.preventDefault(); openExternalUrl(offerData.shippingStatus.labelUrl); } }}
                                className="tp-tl-link"
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Download label
                              </a>
                            )}
                            {offerData.shippingStatus.trackingNumber && (
                              <div className="tp-tl-mono">{offerData.shippingStatus.trackingNumber}</div>
                            )}
                          </>
                        ) : isSeller && offerData.shippingStatus?.confirmationPhoto ? (
                          <div className="tp-tl-gold-row">
                            <button onClick={handleOpenShippingForm} className="tp-tl-gold-btn">Purchase Label</button>
                          </div>
                        ) : (
                          <span className="tp-badge tp-badge-gray">{offerData.shippingStatus?.confirmationPhoto ? 'Label required' : 'Photo required first'}</span>
                        )}
                      </div>
                    </div>

                    {/* Shipping Form - Show when showShippingForm is true */}
                    {showShippingForm && (
                      <div style={{ marginTop: '2rem' }}>
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                          <h3 style={{ fontFamily: 'Nunito, sans-serif', margin: 0, marginBottom: '0.5rem' }}>Purchase Shipping Label</h3>
                          <button
                            onClick={() => setShowShippingForm(false)}
                            style={{
                              background: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              fontSize: '16px',
                              cursor: 'pointer',
                              color: '#6b7280'
                            }}
                          >
                            ✕
                          </button>
                        </div>

                        {/* Package Dimensions Form */}
                        {shippingRates.length === 0 && (
                          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                            <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '0.5rem' }}>Package Dimensions</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Length (inches)</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={packageData.length}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                      setPackageData(prev => ({ ...prev, length: value }));
                                    }
                                  }}
                                  placeholder="0.0"
                                  style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Width (inches)</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={packageData.width}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                      setPackageData(prev => ({ ...prev, width: value }));
                                    }
                                  }}
                                  placeholder="0.0"
                                  style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Height (inches)</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={packageData.height}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                      setPackageData(prev => ({ ...prev, height: value }));
                                    }
                                  }}
                                  placeholder="0.0"
                                  style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '14px' }}>Weight (pounds)</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={packageData.weight}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                      setPackageData(prev => ({ ...prev, weight: value }));
                                    }
                                  }}
                                  placeholder="0.0"
                                  style={{ width: '60px', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', textAlign: 'center' }}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* From Address */}
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                          <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '0.5rem' }}>From Address</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                              <input type="text" placeholder="Name" value={packageData.fromAddress.name} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="Address" value={packageData.fromAddress.street1} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="Apt" value={packageData.fromAddress.street2} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                              <input type="text" placeholder="City" value={packageData.fromAddress.city} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="State" value={packageData.fromAddress.state} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="Zip Code" value={packageData.fromAddress.zip} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                              <input type="email" placeholder="Email" value={packageData.fromAddress.email} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="tel" placeholder="Phone" value={packageData.fromAddress.phone} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                            </div>
                          </div>
                        </div>

                        {/* To Address */}
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                          <h4 style={{ fontFamily: 'Nunito, sans-serif', marginBottom: '0.5rem' }}>To Address</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                              <input type="text" placeholder="Name" value={packageData.toAddress.name} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="Address" value={packageData.toAddress.street1} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="Apt" value={packageData.toAddress.street2} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                              <input type="text" placeholder="City" value={packageData.toAddress.city} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="State" value={packageData.toAddress.state} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="text" placeholder="Zip Code" value={packageData.toAddress.zip} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', width: '100%', maxWidth: '400px' }}>
                              <input type="email" placeholder="Email" value={maskEmail(packageData.toAddress.email)} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                              <input type="tel" placeholder="Phone" value={maskPhone(packageData.toAddress.phone)} readOnly style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '16px', backgroundColor: '#f9fafb', color: '#374151' }} />
                            </div>
                          </div>
                        </div>

                        {/* Get Rates Button */}
                        <div style={{ marginTop: '1rem' }}>
                          <button
                            onClick={handleGetShippingRates}
                            disabled={gettingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight ||
                                      !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city ||
                                      !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                      !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city ||
                                      !packageData.toAddress.state || !packageData.toAddress.zip}
                            style={{
                              backgroundColor: (gettingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight ||
                                               !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city ||
                                               !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                               !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city ||
                                               !packageData.toAddress.state || !packageData.toAddress.zip) ? '#E5E7EB' : '#C9A227',
                              color: 'white',
                              border: 'none',
                              borderRadius: '9999px',
                              padding: '6px 14px',
                              fontFamily: 'Nunito, sans-serif',
                              fontWeight: 'bold',
                              fontSize: '15px',
                              cursor: (gettingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight ||
                                       !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city ||
                                       !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                       !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city ||
                                       !packageData.toAddress.state || !packageData.toAddress.zip) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {gettingRates ? 'Getting Rates...' : 'Get Shipping Rates'}
                          </button>
                        </div>

                        {/* Shipping Rates */}
                        {shippingRates.length > 0 && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '1rem' }}>
                              <h4 style={{ fontFamily: 'Nunito, sans-serif', margin: 0 }}>Available Shipping Options</h4>
                              <ShippingPricesLink onClick={() => setShowShippingPrices(true)} style={{ fontSize: '15px' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                              {Object.entries(shippingRates.reduce((acc, rate) => {
                                const carrier = rate.provider || 'Unknown';
                                if (!acc[carrier]) acc[carrier] = [];
                                acc[carrier].push(rate);
                                return acc;
                              }, {})).map(([carrier, rates]) => (
                                <div key={carrier} style={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '1rem',
                                  backgroundColor: '#f9fafb'
                                }}>
                                  <div style={{ marginBottom: '1rem' }}>
                                    <h5 style={{
                                      fontFamily: 'Nunito, sans-serif',
                                      fontWeight: 'bold',
                                      fontSize: '18px',
                                      color: '#1f2937',
                                      margin: '0'
                                    }}>{carrier}</h5>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {rates.map((rate, index) => (
                                      <div key={index} style={{
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        backgroundColor: '#f9fafb'
                                      }}>
                                        <div style={{ marginBottom: '12px' }}>
                                          <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                                            {rate.servicelevel || 'Standard'}
                                          </div>
                                          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                            {rate.estimated_days} day{rate.estimated_days !== 1 ? 's' : ''} delivery
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Total Cost:</span>
                                            <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#059669' }}>${rate.displayAmount}</span>
                                          </div>
                                          <div style={{
                                            fontSize: '11px',
                                            color: '#059669',
                                            backgroundColor: '#f0fdf4',
                                            padding: '6px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid #bbf7d0'
                                          }}>
                                            ✓ Includes insurance
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => handleCreateCheckout(rate)}
                                          disabled={creatingCheckout}
                                          style={{
                                            width: '100%',
                                            backgroundColor: creatingCheckout ? '#E5E7EB' : '#C9A227',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '9999px',
                                            padding: '6px 14px',
                                            fontFamily: 'Nunito, sans-serif',
                                            fontWeight: 'bold',
                                            fontSize: '15px',
                                            cursor: creatingCheckout ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          {creatingCheckout ? 'Processing...' : 'Purchase Label'}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Item in transit */}
                {isTransitStep && !isCollapsed && (
                  <div className="tp-tl-panel">
                    {offerData.shippingStatus?.trackingNumber && (
                      <button onClick={refreshPurchaseTrackingStatus} disabled={refreshing} className="tp-tl-refresh">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                        {refreshing ? 'Refreshing...' : 'Refresh tracking status'}
                      </button>
                    )}
                    {offerData.shippingStatus?.inTransit && !offerData.shippingStatus?.delivered && offerData.status !== 'complete' && (
                      <div className="tp-tl-help">The buyer marks the package as received once it arrives, releasing the escrowed funds to the seller.</div>
                    )}
                    {offerData.shippingStatus?.trackingNumber ? (
                      <div className="tp-tl-user">
                        <div className="tp-tl-line-item">
                          <span className="lbl">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            Status
                          </span>
                          <span className={`tp-badge ${offerData.shippingStatus?.delivered ? 'tp-badge-green' : offerData.shippingStatus?.inTransit ? 'tp-badge-blue' : 'tp-badge-gray'}`}>{offerData.shippingStatus?.delivered ? 'Delivered' : offerData.shippingStatus?.inTransit ? 'In transit' : 'Label created'}</span>
                        </div>
                        <div className="tp-tl-mono">{offerData.shippingStatus.trackingNumber}</div>
                        {offerData.shippingStatus.currentTrackingStatus && (
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '6px' }}>
                            Status: {typeof offerData.shippingStatus.currentTrackingStatus === 'object' ? offerData.shippingStatus.currentTrackingStatus.status : offerData.shippingStatus.currentTrackingStatus}
                          </div>
                        )}
                        {(offerData.shippingStatus.lastUpdated || offerData.shippingStatus.lastTrackingUpdate) && (
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '4px' }}>
                            Last updated {new Date(((offerData.shippingStatus.lastUpdated || offerData.shippingStatus.lastTrackingUpdate).seconds) * 1000).toLocaleDateString()}
                          </div>
                        )}
                        {offerData.shippingStatus?.trackingHistory && offerData.shippingStatus.trackingHistory.filter(event => typeof event.status === 'string').length > 0 && (
                          <details style={{ fontSize: '12px', marginTop: '8px' }}>
                            <summary className="tp-tl-link" style={{ cursor: 'pointer' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              View history ({offerData.shippingStatus.trackingHistory.filter(event => typeof event.status === 'string').length})
                            </summary>
                            <div style={{ marginTop: '8px', maxHeight: '256px', overflowY: 'auto', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '6px', maxWidth: '100%' }}>
                              {offerData.shippingStatus.trackingHistory.filter(event => typeof event.status === 'string').map((event, hIdx) => (
                                <div key={hIdx} style={{ marginBottom: '8px', padding: '8px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                                  <div style={{ fontWeight: '700', fontSize: '10px', color: '#1f2937', textTransform: 'uppercase' }}>{event.status || 'Unknown'}</div>
                                  {(event.statusDetail || event.status_details) && (<div style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px' }}>{event.statusDetail || event.status_details}</div>)}
                                  {event.location && (<div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>📍 {event.location}</div>)}
                                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>{event.statusDate ? new Date(event.statusDate).toLocaleDateString() : (event.timestamp ? new Date(event.timestamp).toLocaleDateString() : '')}</div>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                        {/* In Transit panel. The separate "Awaiting delivery" step was
      removed 2026-11-01 (owner): it repeated the Delivered badge shown here and
      only added this button, so the button lives with the tracking it confirms.
      Only the buyer may mark it received, and only once the
      carrier has reported it in transit. The write is unchanged
      (shippingStatus.delivered), so the server-side completion, escrow
      timer, feedback and congratulations flow all run exactly as before. */}
                        {offerData.shippingStatus?.delivered && offerData.shippingStatus.deliveredAt && (
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '6px' }}>
                            Delivered {new Date(offerData.shippingStatus.deliveredAt.seconds ? offerData.shippingStatus.deliveredAt.seconds * 1000 : offerData.shippingStatus.deliveredAt).toLocaleDateString()}
                          </div>
                        )}
                        {isBuyer && offerData.status !== 'complete' && !offerData.shippingStatus?.delivered && offerData.paymentStatus?.buyerPaid && offerData.paymentStatus?.sellerPaid && offerData.shippingStatus?.inTransit && (
                          <div className="tp-tl-gold-row tp-tl-gold-row--mark-received">
                            <button
                              onClick={handleMarkDelivered}
                              disabled={markingDelivered}
                              className="tp-tl-gold-btn"
                              style={{ opacity: markingDelivered ? 0.6 : 1 }}
                            >
                              {markingDelivered ? 'Marking Received...' : 'Mark as Received'}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="tp-badge tp-badge-gray">No tracking yet</span>
                    )}
                  </div>
                )}


                {/* Feedback */}
                {isFeedbackStep && !isCollapsed && (
                  <div className="tp-tl-panel">
                    {/* Completion is the real gate — it's what FeedbackForm and
                        submitFeedback both check. Delivery alone was too narrow:
                        a purchase an admin closed after a lost package is
                        complete but never delivered, and those are exactly the
                        cases where feedback matters most. */}
                    {(offerData.shippingStatus?.delivered || offerData.status === 'complete') ? (
                      <FeedbackForm
                        trade={offerData}
                        tradeId={offerId}
                        tradeType="purchase"
                        currentUserId={userId}
                        buyerId={offerData.initiatorId}
                        sellerId={offerData.responderId}
                        onFeedbackSubmitted={refreshPurchaseData}
                        shareLinks={isBuyer ? (
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
                            <button
                              onClick={handleShareToReddit}
                              style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: '500', color: '#FF4500', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              Share on Reddit
                            </button>
                            <button
                              onClick={handleShareToFacebook}
                              style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: '500', color: '#1877F2', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              Share on Facebook
                            </button>
                          </div>
                        ) : undefined}
                      />
                    ) : (
                      <p style={{ color: '#6b7280' }}>Feedback will be available after delivery confirmation.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Footer */}
      <div className="py-4" style={{ paddingBottom: '10px', backgroundColor: '#F7F3EB', marginTop: 'auto' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button
                type="button"
                onClick={() => openContact({ category: 'general' })}
                className="hover:underline"
                style={{ color: '#5A9ECB', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      <ImageOverlay
        isOpen={photoOverlay.isOpen}
        imageUrl={photoOverlay.url}
        imageAlt={photoOverlay.alt}
        onClose={closeConfirmationPhoto}
      />

      {/* Inline shipping-address modal — auto-opens when the user has no
          complete address on file (needed for the Shippo label). */}
      <AddressRequiredModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSaved={() => setShowAddressModal(false)}
        user={firestoreUser}
        context="shipping"
      />

      <ShippingPricesModal
        isOpen={showShippingPrices}
        onClose={() => setShowShippingPrices(false)}
        variant="purchase"
        isBuyer={isBuyer}
      />
    </div>
  );
};

export default PurchaseProgress;